/**
 * <shaftlab-configurator> — de regelmotor van de club-builder, plus het element.
 *
 * Eén bestand draagt motor, UI en CSS (N5, N7). De motor kwam met #41; de weergave is
 * sinds v1.1-08 (#76) de wizard van canvas 3c: vijf stappen, één tegelijk, met een
 * meetellende onderbalk. Ook de opslag van Mijn specs ("Bewaar deze spec") leeft hier.
 *
 * De motor is puur: `(payload, keuzes)` → lijsten met reden, uitlezing, prijs, compleet
 * en fouten. Geen DOM, geen fetch, geen globals, zodat dezelfde module in Node draait
 * (de tests) en straks in de worker (spec #37 §E: één bron, geen afgeleide kopie).
 *
 * De payload is de vorm uit spec #37 §C, gerenderd door snippets/catalogus-json.liquid.
 * Prijzen zijn hele centen in de valuta van de markt.
 */

/* ---------------------------------------------------------------- STRINGS */
/* Elke UI-string van het element staat hier (N14), één object per taal (#86). Engels is de
   basis; elke andere taal draagt precies dezelfde sleutels (test/talen.test.js). Het element
   kiest bij het opstarten de taal van zijn pagina — het `lang` van het dichtstbijzijnde
   element, bij een gastpagina dus die van de gast — met `kiesTaal`. De werkbon is voor de
   werkplaats en blijft Nederlands: die leest `WERKBONTAAL`, niet deze tabellen.
   `decimaal` is het decimaalteken van een maat in inch (`maat`); `clubs` de clubnamen. */

const EN = {
  decimaal: '.',
  clubs: { driver: 'Driver', fw3: 'Fairway 3', fw5: 'Fairway 5', fw7: 'Fairway 7', hy3: 'Hybrid 3', hy4: 'Hybrid 4', hy5: 'Hybrid 5' },
  griplaagdikte: ['standard', 'a touch thicker', 'slightly thicker', 'noticeably thicker', 'much thicker'],

  reden_blokkade: 'This combination does not work in practice.',
  reden_grip_hand: (aantal, hand) =>
    (aantal === 1 ? '1 grip drops out' : aantal + ' grips drop out') +
    ': ' + (hand === 'links' ? 'left' : 'right') + '-handed only.',
  reden_tipping_max: (max) => 'This shaft takes at most ' + maat(max) + ' of tipping.',
  reden_lengte_grens: (ongesneden) => 'Longer than the uncut shaft (' + maat(ongesneden) + ').',

  fout_weg: 'This option is no longer available.',

  stap_titel: { 1: 'Which club is it for?', 2: 'Choose flex and weight', 3: 'Choose your grip', 4: 'Which adapter?', 5: 'Finishing' },
  stap_kort: { 1: 'Club', 2: 'Flex and weight', 3: 'Grip', 4: 'Adapter', 5: 'Finishing' },
  voortgang: 'Steps',
  stap_van: (n, totaal) => 'step ' + n + ' of ' + totaal,
  vorige: 'Back',
  volgende: 'Next',
  som_onderdelen: 'sum of the parts',
  voorbeeldfoto: 'Example photo',
  intro_grip: 'Every grip fits this shaft. Its price is added to the total.',
  intro_adapter: (tip) => 'The adapter goes with the brand of your head. Only ' + tip + '" adapters fit this shaft.',
  intro_afwerking: 'The defaults are set. Only change what you know.',
  overzicht: 'Your custom shaft',
  bewaar: 'Save this spec',
  bewaard: 'Saved in this browser',

  veld_hand: 'Hand',
  veld_club: 'Club',
  hulp_club: 'Which club does this shaft go into? That sets the standard length.',
  veld_flex: 'Flex',
  veld_gewicht: 'Weight',
  hulp_shaft: 'Weight and flex belong together: together they make one item.',
  kies_flex_eerst: 'Choose a flex first; then you will see the weights.',
  reden_gewicht: 'not in this flex',
  veld_adaptermerk: 'Brand of your head',
  veld_adaptergeneratie: 'Generation',
  veld_lengte: 'Length',
  veld_tipping: 'Tipping',
  veld_griplaagdikte: 'Grip build-up',
  veld_griplogo: 'Grip logo',
  veld_spining: 'Spine alignment',

  hand_rechts: 'Right-handed',
  hand_links: 'Left-handed',
  lengte_standaard: 'Standard',
  tipping_geen: 'No tipping',
  tipping_iets: 'a little stiffer',
  tipping_stuk: 'a lot stiffer',
  standaard: 'standard',
  logo_boven: 'Logo up',
  logo_onder: 'Logo down',
  spining_niet: 'No spine alignment',
  spining_wel: 'Align to the spine',
  spining_sub: 'free · print follows the spine',
  laag_sub: (n) => (n === 1 ? '1 extra layer' : n + ' extra layers'),
  alle_merken: 'All brands',
  alle_maten: 'All sizes',
  maat_undersize: 'Undersize',
  maat_standaard: 'Standard',
  maat_midsize: 'Midsize',
  maat_jumbo: 'Jumbo',
  maat_filter: 'Filter by grip size',
  grip_zoek: 'Search by brand or model',
  grip_zoek_label: 'Search grips',

  lengte_fijn: 'Show finer steps',
  lengte_grof: 'Show half steps only',

  uitklap: 'What is this?',
  letop_lengte:
    'Length is a deviation from the standard length with a standard head. What your club actually plays depends on your head and hosel — and we do not know those.',
  letop_kop: 'Is your head not listed? Then we do not carry that adapter (yet).',
  waarom_club: (tip, hybride) =>
    'This shaft has a ' + tip + '" tip and goes into a ' + (hybride ? 'hybrid' : 'wood') + ' with an adapter.',
  waarom_adapters: (aantal, tip) =>
    (aantal === 1 ? '1 adapter fits' : aantal + ' adapters fit') + ' a ' + tip + '" tip.',
  waarom_adapters_club: (aantal, tip, club) =>
    (aantal === 1 ? '1 adapter fits' : aantal + ' adapters fit') + ' a ' + tip + '" tip and a ' + club + '.',
  waarom_generaties: (aantal, merk) =>
    (aantal === 1 ? '1 generation of ' + merk + ' fits' : aantal + ' generations of ' + merk + ' fit') + ' this tip.',
  waarom_grips: (getoond, totaal) => (getoond === totaal ? totaal + ' grips.' : getoond + ' of ' + totaal + ' grips.'),
  waarom_grips_cap: (cap) => 'The first ' + cap + ' are shown here; search or filter for the rest.',
  generaties: (n) => (n === 1 ? '1 generation' : n + ' generations'),
  kies_club_eerst: 'Choose a club first. Then you will see which adapters fit.',
  volgt_uit_club: 'Follows from the club.',

  sv_shaft: 'Shaft',
  sv_adapter: 'Adapter',
  sv_grip: 'Grip',
  sv_lengte: 'Length',
  sv_tipping: 'Tipping',
  sv_griplaagdikte: 'Grip build-up',
  sv_griplogo: 'Grip logo',
  sv_spining: 'Spine alignment',
  sv_levertijd: 'Delivery time',
  sv_totaal: 'Total',
  sv_nog_kiezen: 'still to choose',
  sv_geen: '—',
  levertijd: '1–2 weeks',
  ja: 'yes',
  nee: 'no',
  compleet: 'Complete',
  ontbreekt: (lijst) => 'Still to choose: ' + lijst.join(', '),
  ontbreekt_club: 'club',
  ontbreekt_shaft: 'shaft',
  ontbreekt_adapter: 'adapter',
  ontbreekt_grip: 'grip',
  bouwknop: 'Add to cart',
  bouwknop_bezig: 'Working…',
  maatwerknoot: 'Made to your measurements: no cooling-off period once the workshop starts.',
  vertrouwen: (levertijd) => 'One clubmaker builds it by hand, in ' + levertijd + '.',

  fout_onbouwbaar: 'This combination cannot be built.',
  fout_storing: 'That did not work just now. Please try again in a moment.',
  fout_shaftlink: 'To the shaft page',
};

const NL = {
  decimaal: ',',
  clubs: { driver: 'Driver', fw3: 'Fairway 3', fw5: 'Fairway 5', fw7: 'Fairway 7', hy3: 'Hybride 3', hy4: 'Hybride 4', hy5: 'Hybride 5' },
  griplaagdikte: ['standaard', 'een tikje dikker', 'iets dikker', 'merkbaar dikker', 'fors dikker'],

  reden_blokkade: 'Deze combinatie werkt in de praktijk niet.',
  reden_grip_hand: (aantal, hand) =>
    (aantal === 1 ? '1 grip valt af' : aantal + ' grips vallen af') +
    ': alleen ' + (hand === 'links' ? 'links' : 'rechts') + 'handig.',
  reden_tipping_max: (max) => 'Deze shaft verdraagt maximaal ' + maat(max) + ' tipping.',
  reden_lengte_grens: (ongesneden) =>
    'Langer dan de ongesneden shaft (' + maat(ongesneden) + ').',

  fout_weg: 'Deze keuze is er niet meer.',

  /* De vijf stappen (spec #67 §9, canvas 3c) en de onderbalk */
  stap_titel: { 1: 'Waar speel je hem in?', 2: 'Kies flex en gewicht', 3: 'Kies je grip', 4: 'Welke adapter?', 5: 'Afwerking' },
  stap_kort: { 1: 'Club', 2: 'Flex en gewicht', 3: 'Grip', 4: 'Adapter', 5: 'Afwerking' },
  voortgang: 'Stappen',
  stap_van: (n, totaal) => 'stap ' + n + ' van ' + totaal,
  vorige: 'Vorige',
  volgende: 'Volgende',
  som_onderdelen: 'som van de onderdelen',
  voorbeeldfoto: 'Voorbeeldfoto',
  intro_grip: 'Alle grips passen op deze shaft. De prijs komt bij het totaal.',
  intro_adapter: (tip) => 'De adapter hoort bij het merk van je kop. Alleen ' + tip + '" adapters passen op deze shaft.',
  intro_afwerking: 'Standaardwaarden staan goed. Pas alleen aan wat je weet.',
  overzicht: 'Jouw shaft op maat',
  bewaar: 'Bewaar deze spec',
  bewaard: 'Bewaard in deze browser',

  /* Veldkoppen en hun hulptekst */
  veld_hand: 'Hand',
  veld_club: 'Club',
  hulp_club: 'Waar gaat deze shaft in? Dat bepaalt de standaardlengte.',
  veld_flex: 'Flex',
  veld_gewicht: 'Gewicht',
  hulp_shaft: 'Gewicht en flex horen bij elkaar: samen zijn ze één artikel.',
  kies_flex_eerst: 'Kies eerst een flex; dan zie je de gewichten.',
  reden_gewicht: 'niet in deze flex',
  veld_adaptermerk: 'Merk van je kop',
  veld_adaptergeneratie: 'Generatie',
  veld_lengte: 'Lengte',
  veld_tipping: 'Tipping',
  veld_griplaagdikte: 'Griplaagdikte',
  veld_griplogo: 'Griplogo',
  veld_spining: 'Spining',

  /* Keuzelabels */
  hand_rechts: 'Rechtshandig',
  hand_links: 'Linkshandig',
  lengte_standaard: 'Standaard',
  tipping_geen: 'Geen tipping',
  tipping_iets: 'iets stijver',
  tipping_stuk: 'een stuk stijver',
  standaard: 'standaard',
  logo_boven: 'Logo boven',
  logo_onder: 'Logo onder',
  spining_niet: 'Niet spinen',
  spining_wel: 'Wel spinen',
  spining_sub: 'gratis · opdruk volgt de spine',
  laag_sub: (n) => (n === 1 ? '1 extra laag' : n + ' extra lagen'),
  alle_merken: 'Alle merken',
  alle_maten: 'Alle maten',
  maat_undersize: 'Undersize',
  maat_standaard: 'Standaard',
  maat_midsize: 'Midsize',
  maat_jumbo: 'Jumbo',
  maat_filter: 'Filter op gripmaat',
  grip_zoek: 'Zoek op merk of model',
  grip_zoek_label: 'Zoek grip',

  /* Het lengteraster */
  lengte_fijn: 'Fijnere stappen tonen',
  lengte_grof: 'Alleen halve stappen tonen',

  /* Uitleg, waarom en let op */
  uitklap: 'Wat is dit?',
  letop_lengte:
    'Lengte is een afwijking van de standaardlengte bij een standaardkop. Wat jouw club straks écht speelt, hangt af van jouw kop en hosel — die kennen wij niet.',
  letop_kop: 'Staat jouw kop er niet bij? Dan voeren wij die adapter (nog) niet.',
  waarom_club: (tip, hybride) =>
    'Deze shaft heeft een tip van ' + tip + '" en gaat met een adapter in een ' +
    (hybride ? 'hybride.' : 'wood.'),
  waarom_adapters: (aantal, tip) =>
    (aantal === 1 ? '1 adapter past' : aantal + ' adapters passen') + ' op een tip van ' + tip + '".',
  /* De waaromregel als de Club de lijst korter maakt dan de tip alleen zou doen. */
  waarom_adapters_club: (aantal, tip, club) =>
    (aantal === 1 ? '1 adapter past' : aantal + ' adapters passen') +
    ' op een tip van ' + tip + '" én op een ' + club.toLowerCase() + '.',
  waarom_generaties: (aantal, merk) =>
    (aantal === 1 ? '1 generatie' : aantal + ' generaties') + ' van ' + merk + ' passen op deze tip.',
  waarom_grips: (getoond, totaal) =>
    getoond === totaal
      ? totaal + ' grips.'
      : getoond + ' van de ' + totaal + ' grips.',
  waarom_grips_cap: (cap) => 'De eerste ' + cap + ' staan hier; zoek of filter voor de rest.',
  generaties: (n) => (n === 1 ? '1 generatie' : n + ' generaties'),
  kies_club_eerst: 'Kies eerst een club. Daarna zie je welke adapters passen.',
  volgt_uit_club: 'Volgt uit de club.',

  /* Overzicht en onderbalk */
  sv_shaft: 'Shaft',
  sv_adapter: 'Adapter',
  sv_grip: 'Grip',
  sv_lengte: 'Lengte',
  sv_tipping: 'Tipping',
  sv_griplaagdikte: 'Griplaagdikte',
  sv_griplogo: 'Griplogo',
  sv_spining: 'Spining',
  sv_levertijd: 'Levertijd',
  sv_totaal: 'Totaal',
  sv_nog_kiezen: 'nog kiezen',
  sv_geen: '—',
  /* Val-terug; de payload draagt de thema-instelling Levertijd (spec #67 §2). */
  levertijd: '1–2 weken',
  ja: 'ja',
  nee: 'nee',
  compleet: 'Compleet',
  ontbreekt: (lijst) => 'Nog kiezen: ' + lijst.join(', '),
  ontbreekt_club: 'club',
  ontbreekt_shaft: 'shaft',
  ontbreekt_adapter: 'adapter',
  ontbreekt_grip: 'grip',
  bouwknop: 'In de winkelwagen',
  bouwknop_bezig: 'Bezig…',
  maatwerknoot: 'Bewerkt op jouw maat: geen bedenktijd zodra de werkplaats begint.',
  vertrouwen: (levertijd) => 'Eén clubmaker bouwt hem met de hand, in ' + levertijd + '.',

  /* Foutstaat — docs/prijs-en-voorraadcontract.md §4, gevallen 7 en 8 */
  fout_onbouwbaar: 'Deze combinatie kan niet gebouwd worden.',
  fout_storing: 'Dit lukt even niet. Probeer het zo nog eens.',
  fout_shaftlink: 'Naar de shaftpagina',
};

const DE = {
  decimaal: ',',
  clubs: { driver: 'Driver', fw3: 'Fairway 3', fw5: 'Fairway 5', fw7: 'Fairway 7', hy3: 'Hybrid 3', hy4: 'Hybrid 4', hy5: 'Hybrid 5' },
  griplaagdikte: ['Standard', 'einen Hauch dicker', 'etwas dicker', 'merklich dicker', 'deutlich dicker'],

  reden_blokkade: 'Diese Kombination funktioniert in der Praxis nicht.',
  reden_grip_hand: (aantal, hand) =>
    (aantal === 1 ? '1 Grip fällt weg' : aantal + ' Grips fallen weg') +
    ': nur ' + (hand === 'links' ? 'linkshändig' : 'rechtshändig') + '.',
  reden_tipping_max: (max) => 'Dieser Shaft verträgt maximal ' + maat(max) + ' Tipping.',
  reden_lengte_grens: (ongesneden) => 'Länger als der ungekürzte Shaft (' + maat(ongesneden) + ').',

  fout_weg: 'Diese Option ist nicht mehr verfügbar.',

  stap_titel: { 1: 'Für welchen Club?', 2: 'Flex und Gewicht wählen', 3: 'Grip wählen', 4: 'Welcher Adapter?', 5: 'Ausführung' },
  stap_kort: { 1: 'Club', 2: 'Flex und Gewicht', 3: 'Grip', 4: 'Adapter', 5: 'Ausführung' },
  voortgang: 'Schritte',
  stap_van: (n, totaal) => 'Schritt ' + n + ' von ' + totaal,
  vorige: 'Zurück',
  volgende: 'Weiter',
  som_onderdelen: 'Summe der Teile',
  voorbeeldfoto: 'Beispielfoto',
  intro_grip: 'Jeder Grip passt auf diesen Shaft. Der Preis kommt zur Summe dazu.',
  intro_adapter: (tip) => 'Der Adapter passt zur Marke Ihres Kopfes. Nur ' + tip + '" Adapter passen auf diesen Shaft.',
  intro_afwerking: 'Die Standardwerte sind richtig eingestellt. Ändern Sie nur, was Sie wissen.',
  overzicht: 'Ihr Shaft nach Maß',
  bewaar: 'Diese Spec speichern',
  bewaard: 'In diesem Browser gespeichert',

  veld_hand: 'Hand',
  veld_club: 'Club',
  hulp_club: 'Wofür ist dieser Shaft? Das bestimmt die Standardlänge.',
  veld_flex: 'Flex',
  veld_gewicht: 'Gewicht',
  hulp_shaft: 'Gewicht und Flex gehören zusammen: zusammen bilden sie einen Artikel.',
  kies_flex_eerst: 'Wählen Sie zuerst einen Flex; dann sehen Sie die Gewichte.',
  reden_gewicht: 'nicht in diesem Flex',
  veld_adaptermerk: 'Marke Ihres Kopfes',
  veld_adaptergeneratie: 'Generation',
  veld_lengte: 'Länge',
  veld_tipping: 'Tipping',
  veld_griplaagdikte: 'Griffaufbau',
  veld_griplogo: 'Grip-Logo',
  veld_spining: 'Spine-Ausrichtung',

  hand_rechts: 'Rechtshändig',
  hand_links: 'Linkshändig',
  lengte_standaard: 'Standard',
  tipping_geen: 'Kein Tipping',
  tipping_iets: 'etwas steifer',
  tipping_stuk: 'deutlich steifer',
  standaard: 'Standard',
  logo_boven: 'Logo oben',
  logo_onder: 'Logo unten',
  spining_niet: 'Nicht ausrichten',
  spining_wel: 'An der Spine ausrichten',
  spining_sub: 'kostenlos · Aufdruck folgt der Spine',
  laag_sub: (n) => (n === 1 ? '1 zusätzliche Lage' : n + ' zusätzliche Lagen'),
  alle_merken: 'Alle Marken',
  alle_maten: 'Alle Größen',
  maat_undersize: 'Undersize',
  maat_standaard: 'Standard',
  maat_midsize: 'Midsize',
  maat_jumbo: 'Jumbo',
  maat_filter: 'Nach Griffgröße filtern',
  grip_zoek: 'Suche nach Marke oder Modell',
  grip_zoek_label: 'Grip suchen',

  lengte_fijn: 'Feinere Schritte anzeigen',
  lengte_grof: 'Nur halbe Schritte anzeigen',

  uitklap: 'Was ist das?',
  letop_lengte:
    'Die Länge ist eine Abweichung von der Standardlänge bei einem Standardkopf. Was Ihr Club später wirklich spielt, hängt von Ihrem Kopf und Hosel ab — die kennen wir nicht.',
  letop_kop: 'Ihr Kopf ist nicht dabei? Dann führen wir diesen Adapter (noch) nicht.',
  waarom_club: (tip, hybride) =>
    'Dieser Shaft hat einen Tip von ' + tip + '" und geht mit einem Adapter in einen ' +
    (hybride ? 'Hybrid.' : 'Wood.'),
  waarom_adapters: (aantal, tip) =>
    (aantal === 1 ? '1 Adapter passt' : aantal + ' Adapter passen') + ' auf einen Tip von ' + tip + '".',
  waarom_adapters_club: (aantal, tip, club) =>
    (aantal === 1 ? '1 Adapter passt' : aantal + ' Adapter passen') +
    ' auf einen Tip von ' + tip + '" und auf einen ' + club + '.',
  waarom_generaties: (aantal, merk) =>
    (aantal === 1 ? '1 Generation von ' + merk + ' passt' : aantal + ' Generationen von ' + merk + ' passen') + ' auf diesen Tip.',
  waarom_grips: (getoond, totaal) =>
    getoond === totaal
      ? totaal + ' Grips.'
      : getoond + ' von ' + totaal + ' Grips.',
  waarom_grips_cap: (cap) => 'Die ersten ' + cap + ' werden hier angezeigt; suchen oder filtern Sie für den Rest.',
  generaties: (n) => (n === 1 ? '1 Generation' : n + ' Generationen'),
  kies_club_eerst: 'Wählen Sie zuerst einen Club. Danach sehen Sie, welche Adapter passen.',
  volgt_uit_club: 'Folgt aus dem Club.',

  sv_shaft: 'Shaft',
  sv_adapter: 'Adapter',
  sv_grip: 'Grip',
  sv_lengte: 'Länge',
  sv_tipping: 'Tipping',
  sv_griplaagdikte: 'Griffaufbau',
  sv_griplogo: 'Grip-Logo',
  sv_spining: 'Spine-Ausrichtung',
  sv_levertijd: 'Lieferzeit',
  sv_totaal: 'Summe',
  sv_nog_kiezen: 'noch wählen',
  sv_geen: '—',
  levertijd: '1–2 Wochen',
  ja: 'ja',
  nee: 'nein',
  compleet: 'Vollständig',
  ontbreekt: (lijst) => 'Noch zu wählen: ' + lijst.join(', '),
  ontbreekt_club: 'Club',
  ontbreekt_shaft: 'Shaft',
  ontbreekt_adapter: 'Adapter',
  ontbreekt_grip: 'Grip',
  bouwknop: 'In den Warenkorb',
  bouwknop_bezig: 'Einen Moment…',
  maatwerknoot: 'Nach Ihrem Maß gefertigt: kein Widerrufsrecht, sobald die Werkstatt beginnt.',
  vertrouwen: (levertijd) => 'Ein Clubmaker baut ihn von Hand, in ' + levertijd + '.',

  fout_onbouwbaar: 'Diese Kombination kann nicht gebaut werden.',
  fout_storing: 'Das hat gerade nicht geklappt. Bitte versuchen Sie es gleich noch einmal.',
  fout_shaftlink: 'Zur Shaft-Seite',
};

const FR = {
  decimaal: ',',
  clubs: { driver: 'Driver', fw3: 'Fairway 3', fw5: 'Fairway 5', fw7: 'Fairway 7', hy3: 'Hybride 3', hy4: 'Hybride 4', hy5: 'Hybride 5' },
  griplaagdikte: ['standard', 'un peu plus épais', "légèrement plus épais", 'nettement plus épais', 'beaucoup plus épais'],

  reden_blokkade: 'Cette combinaison ne fonctionne pas dans la pratique.',
  reden_grip_hand: (aantal, hand) =>
    (aantal === 1 ? '1 grip est exclu' : aantal + ' grips sont exclus') +
    ' : réservé aux ' + (hand === 'links' ? 'gauchers' : 'droitiers') + '.',
  reden_tipping_max: (max) => 'Ce shaft accepte au maximum ' + maat(max) + ' de tipping.',
  reden_lengte_grens: (ongesneden) => 'Plus long que le shaft non coupé (' + maat(ongesneden) + ').',

  fout_weg: "Ce choix n'est plus disponible.",

  stap_titel: { 1: 'Pour quel club ?', 2: 'Choisissez flex et poids', 3: 'Choisissez votre grip', 4: 'Quel adaptateur ?', 5: 'Finition' },
  stap_kort: { 1: 'Club', 2: 'Flex et poids', 3: 'Grip', 4: 'Adaptateur', 5: 'Finition' },
  voortgang: 'Étapes',
  stap_van: (n, totaal) => 'étape ' + n + ' sur ' + totaal,
  vorige: 'Précédent',
  volgende: 'Suivant',
  som_onderdelen: 'somme des composants',
  voorbeeldfoto: "Photo d'exemple",
  intro_grip: "Tous les grips s'adaptent à ce shaft. Son prix s'ajoute au total.",
  intro_adapter: (tip) => "L'adaptateur correspond à la marque de votre tête. Seuls les adaptateurs " + tip + '" s\'adaptent à ce shaft.',
  intro_afwerking: 'Les valeurs par défaut sont réglées. Ne modifiez que ce que vous connaissez.',
  overzicht: 'Votre shaft sur mesure',
  bewaar: 'Enregistrer cette spec',
  bewaard: 'Enregistré dans ce navigateur',

  veld_hand: 'Main',
  veld_club: 'Club',
  hulp_club: 'Dans quel club va ce shaft ? Cela détermine la longueur standard.',
  veld_flex: 'Flex',
  veld_gewicht: 'Poids',
  hulp_shaft: 'Le poids et le flex vont ensemble : ils forment un seul article.',
  kies_flex_eerst: "Choisissez d'abord un flex ; vous verrez ensuite les poids.",
  reden_gewicht: 'pas dans ce flex',
  veld_adaptermerk: 'Marque de votre tête',
  veld_adaptergeneratie: 'Génération',
  veld_lengte: 'Longueur',
  veld_tipping: 'Tipping',
  veld_griplaagdikte: 'Épaisseur sous grip',
  veld_griplogo: 'Logo du grip',
  veld_spining: 'Alignement du spine',

  hand_rechts: 'Droitier',
  hand_links: 'Gaucher',
  lengte_standaard: 'Standard',
  tipping_geen: 'Pas de tipping',
  tipping_iets: 'un peu plus rigide',
  tipping_stuk: 'beaucoup plus rigide',
  standaard: 'standard',
  logo_boven: 'Logo en haut',
  logo_onder: 'Logo en bas',
  spining_niet: 'Sans alignement du spine',
  spining_wel: 'Aligner sur le spine',
  spining_sub: "gratuit · l'impression suit le spine",
  laag_sub: (n) => (n === 1 ? '1 couche supplémentaire' : n + ' couches supplémentaires'),
  alle_merken: 'Toutes les marques',
  alle_maten: 'Toutes les tailles',
  maat_undersize: 'Undersize',
  maat_standaard: 'Standard',
  maat_midsize: 'Midsize',
  maat_jumbo: 'Jumbo',
  maat_filter: 'Filtrer par taille de grip',
  grip_zoek: 'Rechercher par marque ou modèle',
  grip_zoek_label: 'Rechercher un grip',

  lengte_fijn: 'Afficher des paliers plus fins',
  lengte_grof: 'Afficher uniquement les demi-paliers',

  uitklap: "Qu'est-ce que c'est ?",
  letop_lengte:
    "La longueur est un écart par rapport à la longueur standard pour une tête standard. Ce que votre club jouera réellement dépend de votre tête et de votre hosel — que nous ne connaissons pas.",
  letop_kop: "Votre tête n'est pas dans la liste ? Nous ne proposons pas (encore) cet adaptateur.",
  waarom_club: (tip, hybride) =>
    'Ce shaft a un tip de ' + tip + '" et s\'installe avec un adaptateur dans un ' +
    (hybride ? 'hybride.' : 'bois.'),
  waarom_adapters: (aantal, tip) =>
    (aantal === 1 ? "1 adaptateur s'adapte" : aantal + " adaptateurs s'adaptent") + ' à un tip de ' + tip + '".',
  waarom_adapters_club: (aantal, tip, club) =>
    (aantal === 1 ? "1 adaptateur s'adapte" : aantal + " adaptateurs s'adaptent") +
    ' à un tip de ' + tip + '" et à un ' + club.toLowerCase() + '.',
  waarom_generaties: (aantal, merk) =>
    aantal === 1
      ? '1 génération de ' + merk + " s'adapte à ce tip."
      : aantal + ' générations de ' + merk + " s'adaptent à ce tip.",
  waarom_grips: (getoond, totaal) =>
    getoond === totaal
      ? totaal + ' grips.'
      : getoond + ' sur ' + totaal + ' grips.',
  waarom_grips_cap: (cap) => 'Les ' + cap + ' premiers sont affichés ici ; recherchez ou filtrez pour le reste.',
  generaties: (n) => (n === 1 ? '1 génération' : n + ' générations'),
  kies_club_eerst: "Choisissez d'abord un club. Vous verrez ensuite quels adaptateurs s'adaptent.",
  volgt_uit_club: 'Découle du club.',

  sv_shaft: 'Shaft',
  sv_adapter: 'Adaptateur',
  sv_grip: 'Grip',
  sv_lengte: 'Longueur',
  sv_tipping: 'Tipping',
  sv_griplaagdikte: 'Épaisseur sous grip',
  sv_griplogo: 'Logo du grip',
  sv_spining: 'Alignement du spine',
  sv_levertijd: 'Délai de livraison',
  sv_totaal: 'Total',
  sv_nog_kiezen: 'encore à choisir',
  sv_geen: '—',
  levertijd: '1 à 2 semaines',
  ja: 'oui',
  nee: 'non',
  compleet: 'Complet',
  ontbreekt: (lijst) => 'Encore à choisir : ' + lijst.join(', '),
  ontbreekt_club: 'club',
  ontbreekt_shaft: 'shaft',
  ontbreekt_adapter: 'adaptateur',
  ontbreekt_grip: 'grip',
  bouwknop: 'Ajouter au panier',
  bouwknop_bezig: 'En cours…',
  maatwerknoot: "Fabriqué à vos mesures : le délai de rétractation prend fin dès que l'atelier commence.",
  vertrouwen: (levertijd) => 'Un seul clubmaker le fabrique à la main, en ' + levertijd + '.',

  fout_onbouwbaar: 'Cette combinaison ne peut pas être construite.',
  fout_storing: "Cela n'a pas fonctionné. Réessayez dans un instant.",
  fout_shaftlink: 'Vers la page du shaft',
};

const IT = {
  decimaal: ',',
  clubs: { driver: 'Driver', fw3: 'Fairway 3', fw5: 'Fairway 5', fw7: 'Fairway 7', hy3: 'Ibrido 3', hy4: 'Ibrido 4', hy5: 'Ibrido 5' },
  griplaagdikte: ['standard', 'un filo più spesso', 'leggermente più spesso', 'notevolmente più spesso', 'molto più spesso'],

  reden_blokkade: 'Questa combinazione non funziona nella pratica.',
  reden_grip_hand: (aantal, hand) =>
    (aantal === 1 ? '1 grip non è disponibile' : aantal + ' grip non sono disponibili') +
    ': ' + (hand === 'links' ? 'solo per mancini' : 'solo per destrimani') + '.',
  reden_tipping_max: (max) => 'Questo shaft accetta al massimo ' + maat(max) + ' di tipping.',
  reden_lengte_grens: (ongesneden) => 'Più lungo dello shaft non tagliato (' + maat(ongesneden) + ').',

  fout_weg: 'Questa opzione non è più disponibile.',

  stap_titel: { 1: 'Per quale club è?', 2: 'Scegli flex e peso', 3: 'Scegli il tuo grip', 4: 'Quale adapter?', 5: 'Finitura' },
  stap_kort: { 1: 'Club', 2: 'Flex e peso', 3: 'Grip', 4: 'Adapter', 5: 'Finitura' },
  voortgang: 'Passaggi',
  stap_van: (n, totaal) => 'passaggio ' + n + ' di ' + totaal,
  vorige: 'Indietro',
  volgende: 'Avanti',
  som_onderdelen: 'somma dei componenti',
  voorbeeldfoto: "Foto d'esempio",
  intro_grip: 'Ogni grip si adatta a questo shaft. Il prezzo si aggiunge al totale.',
  intro_adapter: (tip) => 'L\'adapter corrisponde al marchio della tua testa. Solo gli adapter da ' + tip + '" si adattano a questo shaft.',
  intro_afwerking: 'I valori predefiniti sono già impostati. Cambia solo ciò che sai.',
  overzicht: 'Il tuo shaft su misura',
  bewaar: 'Salva questa spec',
  bewaard: 'Salvata in questo browser',

  veld_hand: 'Mano',
  veld_club: 'Club',
  hulp_club: 'In quale club va questo shaft? Determina la lunghezza standard.',
  veld_flex: 'Flex',
  veld_gewicht: 'Peso',
  hulp_shaft: 'Peso e flex vanno insieme: insieme formano un unico articolo.',
  kies_flex_eerst: 'Scegli prima un flex; poi vedrai i pesi.',
  reden_gewicht: 'non in questo flex',
  veld_adaptermerk: 'Marchio della tua testa',
  veld_adaptergeneratie: 'Generazione',
  veld_lengte: 'Lunghezza',
  veld_tipping: 'Tipping',
  veld_griplaagdikte: 'Spessore sotto il grip',
  veld_griplogo: 'Logo del grip',
  veld_spining: 'Allineamento dello spine',

  hand_rechts: 'Destrimano',
  hand_links: 'Mancino',
  lengte_standaard: 'Standard',
  tipping_geen: 'Nessun tipping',
  tipping_iets: 'un po\' più rigido',
  tipping_stuk: 'molto più rigido',
  standaard: 'standard',
  logo_boven: 'Logo in alto',
  logo_onder: 'Logo in basso',
  spining_niet: 'Senza allineamento dello spine',
  spining_wel: 'Allinea allo spine',
  spining_sub: 'gratuito · la stampa segue lo spine',
  laag_sub: (n) => (n === 1 ? '1 strato extra' : n + ' strati extra'),
  alle_merken: 'Tutti i marchi',
  alle_maten: 'Tutte le misure',
  maat_undersize: 'Undersize',
  maat_standaard: 'Standard',
  maat_midsize: 'Midsize',
  maat_jumbo: 'Jumbo',
  maat_filter: 'Filtra per misura del grip',
  grip_zoek: 'Cerca per marchio o modello',
  grip_zoek_label: 'Cerca grip',

  lengte_fijn: 'Mostra passaggi più fini',
  lengte_grof: 'Mostra solo mezzi passaggi',

  uitklap: 'Cos\'è questo?',
  letop_lengte:
    'La lunghezza è uno scostamento dalla lunghezza standard con una testa standard. Ciò che il tuo club gioca davvero dipende dalla tua testa e dal tuo hosel — che noi non conosciamo.',
  letop_kop: 'La tua testa non è in elenco? Allora non trattiamo (ancora) quell\'adapter.',
  waarom_club: (tip, hybride) =>
    'Questo shaft ha una punta da ' + tip + '" e si monta con un adapter su un ' +
    (hybride ? 'ibrido.' : 'legno.'),
  waarom_adapters: (aantal, tip) =>
    (aantal === 1 ? '1 adapter si adatta' : aantal + ' adapter si adattano') + ' a una punta da ' + tip + '".',
  waarom_adapters_club: (aantal, tip, club) =>
    (aantal === 1 ? '1 adapter si adatta' : aantal + ' adapter si adattano') +
    ' a una punta da ' + tip + '" e a un ' + club.toLowerCase() + '.',
  waarom_generaties: (aantal, merk) =>
    (aantal === 1 ? '1 generazione di ' + merk + ' si adatta' : aantal + ' generazioni di ' + merk + ' si adattano') + ' a questa punta.',
  waarom_grips: (getoond, totaal) =>
    getoond === totaal
      ? totaal + ' grip.'
      : getoond + ' di ' + totaal + ' grip.',
  waarom_grips_cap: (cap) => 'I primi ' + cap + ' sono mostrati qui; cerca o filtra per il resto.',
  generaties: (n) => (n === 1 ? '1 generazione' : n + ' generazioni'),
  kies_club_eerst: 'Scegli prima un club. Poi vedrai quali adapter si adattano.',
  volgt_uit_club: 'Segue dal club.',

  sv_shaft: 'Shaft',
  sv_adapter: 'Adapter',
  sv_grip: 'Grip',
  sv_lengte: 'Lunghezza',
  sv_tipping: 'Tipping',
  sv_griplaagdikte: 'Spessore sotto il grip',
  sv_griplogo: 'Logo del grip',
  sv_spining: 'Allineamento dello spine',
  sv_levertijd: 'Tempi di consegna',
  sv_totaal: 'Totale',
  sv_nog_kiezen: 'ancora da scegliere',
  sv_geen: '—',
  levertijd: '1–2 settimane',
  ja: 'sì',
  nee: 'no',
  compleet: 'Completo',
  ontbreekt: (lijst) => 'Ancora da scegliere: ' + lijst.join(', '),
  ontbreekt_club: 'club',
  ontbreekt_shaft: 'shaft',
  ontbreekt_adapter: 'adapter',
  ontbreekt_grip: 'grip',
  bouwknop: 'Aggiungi al carrello',
  bouwknop_bezig: 'In corso…',
  maatwerknoot: 'Costruito su tua misura: nessun diritto di recesso non appena il laboratorio inizia il lavoro.',
  vertrouwen: (levertijd) => 'Un solo clubmaker lo costruisce a mano, in ' + levertijd + '.',

  fout_onbouwbaar: 'Questa combinazione non può essere costruita.',
  fout_storing: 'Non ha funzionato proprio ora. Riprova tra un momento.',
  fout_shaftlink: 'Alla pagina dello shaft',
};

const ES = {
  decimaal: ',',
  clubs: { driver: 'Driver', fw3: 'Madera 3', fw5: 'Madera 5', fw7: 'Madera 7', hy3: 'Híbrido 3', hy4: 'Híbrido 4', hy5: 'Híbrido 5' },
  griplaagdikte: ['estándar', 'un poco más grueso', 'algo más grueso', 'notablemente más grueso', 'mucho más grueso'],

  reden_blokkade: 'Esta combinación no funciona en la práctica.',
  reden_grip_hand: (aantal, hand) =>
    (aantal === 1 ? '1 grip no encaja' : aantal + ' grips no encajan') +
    ': solo para ' + (hand === 'links' ? 'zurdos' : 'diestros') + '.',
  reden_tipping_max: (max) => 'Esta varilla admite como máximo ' + maat(max) + ' de tipping.',
  reden_lengte_grens: (ongesneden) => 'Más larga que la varilla sin cortar (' + maat(ongesneden) + ').',

  fout_weg: 'Esta opción ya no está disponible.',

  stap_titel: { 1: '¿Para qué palo es?', 2: 'Elige flex y peso', 3: 'Elige tu grip', 4: '¿Qué adaptador?', 5: 'Acabado' },
  stap_kort: { 1: 'Palo', 2: 'Flex y peso', 3: 'Grip', 4: 'Adaptador', 5: 'Acabado' },
  voortgang: 'Pasos',
  stap_van: (n, totaal) => 'paso ' + n + ' de ' + totaal,
  vorige: 'Anterior',
  volgende: 'Siguiente',
  som_onderdelen: 'suma de las piezas',
  voorbeeldfoto: 'Foto de ejemplo',
  intro_grip: 'Todos los grips encajan en esta varilla. Su precio se suma al total.',
  intro_adapter: (tip) => 'El adaptador va según la marca de tu cabeza. Solo los adaptadores de ' + tip + '" encajan en esta varilla.',
  intro_afwerking: 'Los valores por defecto ya están bien. Cambia solo lo que sepas con certeza.',
  overzicht: 'Tu varilla a medida',
  bewaar: 'Guardar esta spec',
  bewaard: 'Guardado en este navegador',

  veld_hand: 'Mano',
  veld_club: 'Palo',
  hulp_club: '¿En qué palo va esta varilla? Eso determina la longitud estándar.',
  veld_flex: 'Flex',
  veld_gewicht: 'Peso',
  hulp_shaft: 'Peso y flex van juntos: entre los dos forman un solo artículo.',
  kies_flex_eerst: 'Elige primero un flex; después verás los pesos.',
  reden_gewicht: 'no en este flex',
  veld_adaptermerk: 'Marca de tu cabeza',
  veld_adaptergeneratie: 'Generación',
  veld_lengte: 'Longitud',
  veld_tipping: 'Tipping',
  veld_griplaagdikte: 'Grosor bajo el grip',
  veld_griplogo: 'Logo del grip',
  veld_spining: 'Alineación del spine',

  hand_rechts: 'Diestro',
  hand_links: 'Zurdo',
  lengte_standaard: 'Estándar',
  tipping_geen: 'Sin tipping',
  tipping_iets: 'un poco más rígida',
  tipping_stuk: 'bastante más rígida',
  standaard: 'estándar',
  logo_boven: 'Logo arriba',
  logo_onder: 'Logo abajo',
  spining_niet: 'Sin alineación del spine',
  spining_wel: 'Alinear al spine',
  spining_sub: 'gratis · el logo sigue el spine',
  laag_sub: (n) => (n === 1 ? '1 capa extra' : n + ' capas extra'),
  alle_merken: 'Todas las marcas',
  alle_maten: 'Todas las tallas',
  maat_undersize: 'Undersize',
  maat_standaard: 'Estándar',
  maat_midsize: 'Midsize',
  maat_jumbo: 'Jumbo',
  maat_filter: 'Filtrar por talla de grip',
  grip_zoek: 'Busca por marca o modelo',
  grip_zoek_label: 'Buscar grips',

  lengte_fijn: 'Mostrar pasos más finos',
  lengte_grof: 'Mostrar solo medios pasos',

  uitklap: '¿Qué es esto?',
  letop_lengte:
    'La longitud es una desviación respecto a la longitud estándar con una cabeza estándar. Lo que tu palo realmente juega depende de tu cabeza y tu hosel — y eso no lo sabemos nosotros.',
  letop_kop: '¿No encuentras tu cabeza? Entonces (todavía) no tenemos ese adaptador.',
  waarom_club: (tip, hybride) =>
    'Esta varilla tiene un tip de ' + tip + '" y va con un adaptador en ' + (hybride ? 'un híbrido' : 'una madera') + '.',
  waarom_adapters: (aantal, tip) =>
    (aantal === 1 ? '1 adaptador encaja' : aantal + ' adaptadores encajan') + ' en un tip de ' + tip + '".',
  waarom_adapters_club: (aantal, tip, club) =>
    (aantal === 1 ? '1 adaptador encaja' : aantal + ' adaptadores encajan') +
    ' en un tip de ' + tip + '" y en ' + club.toLowerCase() + '.',
  waarom_generaties: (aantal, merk) =>
    (aantal === 1 ? '1 generación de ' + merk + ' encaja' : aantal + ' generaciones de ' + merk + ' encajan') + ' en este tip.',
  waarom_grips: (getoond, totaal) => (getoond === totaal ? totaal + ' grips.' : getoond + ' de ' + totaal + ' grips.'),
  waarom_grips_cap: (cap) => 'Aquí se muestran los primeros ' + cap + '; busca o filtra para ver el resto.',
  generaties: (n) => (n === 1 ? '1 generación' : n + ' generaciones'),
  kies_club_eerst: 'Elige primero un palo. Después verás qué adaptadores encajan.',
  volgt_uit_club: 'Se deduce del palo.',

  sv_shaft: 'Varilla',
  sv_adapter: 'Adaptador',
  sv_grip: 'Grip',
  sv_lengte: 'Longitud',
  sv_tipping: 'Tipping',
  sv_griplaagdikte: 'Grosor bajo el grip',
  sv_griplogo: 'Logo del grip',
  sv_spining: 'Alineación del spine',
  sv_levertijd: 'Plazo de entrega',
  sv_totaal: 'Total',
  sv_nog_kiezen: 'aún por elegir',
  sv_geen: '—',
  levertijd: '1–2 semanas',
  ja: 'sí',
  nee: 'no',
  compleet: 'Completo',
  ontbreekt: (lijst) => 'Aún por elegir: ' + lijst.join(', '),
  ontbreekt_club: 'palo',
  ontbreekt_shaft: 'varilla',
  ontbreekt_adapter: 'adaptador',
  ontbreekt_grip: 'grip',
  bouwknop: 'Añadir al carrito',
  bouwknop_bezig: 'Un momento…',
  maatwerknoot: 'Hecho a tu medida: sin derecho de desistimiento en cuanto el taller empieza.',
  vertrouwen: (levertijd) => 'Un armador la construye a mano, en ' + levertijd + '.',

  fout_onbouwbaar: 'Esta combinación no se puede construir.',
  fout_storing: 'Eso no ha funcionado ahora mismo. Inténtalo de nuevo en un momento.',
  fout_shaftlink: 'A la página de la varilla',
};

export const TALEN = { en: EN, nl: NL, de: DE, fr: FR, it: IT, es: ES };

/* De tabel van de actieve taal. Een live binding: wie `STRINGS` importeert, ziet de taal
   die `kiesTaal` koos. Zonder keuze Engels, de basis. */
export let STRINGS = EN;

/**
 * Kies de taal op een taalcode als `de` of `pt-BR`; een onbekende taal wordt Engels.
 * `teksten` zijn de overschrijvingen uit de locale-bestanden van het thema (zie TEKSTEN
 * hieronder); zonder blijven de tabellen hierboven staan, zoals buiten Shopify.
 */
export function kiesTaal(code, teksten) {
  const basis = String(code || '').toLowerCase().split('-')[0];
  STRINGS = TALEN[basis] || EN;
  if (teksten) STRINGS = metTeksten(STRINGS, teksten);
  return STRINGS;
}

/* ---------------------------------------------------- TEKSTEN VAN DE EIGENAAR */
/* De eigenaar past configuratorteksten aan in Shopify: Online Store → Themes → Edit default
   theme content, per taal (30 sep 2026). Die teksten staan onder `configurator.*` in
   locales/*.json, komen via snippets/catalogus-json.liquid als `teksten` in de payload en
   winnen daar van de tabellen hierboven. De tabellen blijven de standaard buiten Shopify
   (Golf Direction, #32) en het vangnet: een leeg veld, of een zin zonder een verplichte
   [plaatshouder], valt terug op de standaard.

   De sleutels zijn Engels en gegroepeerd, omdat de editor ze als label toont.
   scripts/configurator-teksten.mjs vult de locale-bestanden uit deze tabellen aan en
   overschrijft nooit wat er al staat. */

/* Vaste teksten: locale-sleutel → pad in de tabel. */
export const TEKSTEN = {
  'steps.title_1': ['stap_titel', 1],
  'steps.title_2': ['stap_titel', 2],
  'steps.title_3': ['stap_titel', 3],
  'steps.title_4': ['stap_titel', 4],
  'steps.title_5': ['stap_titel', 5],
  'steps.tab_1': ['stap_kort', 1],
  'steps.tab_2': ['stap_kort', 2],
  'steps.tab_3': ['stap_kort', 3],
  'steps.tab_4': ['stap_kort', 4],
  'steps.tab_5': ['stap_kort', 5],
  'steps.progress_label': ['voortgang'],
  'steps.back_button': ['vorige'],
  'steps.next_button': ['volgende'],
  'steps.overview_title': ['overzicht'],
  'steps.save_button': ['bewaar'],
  'steps.saved_note': ['bewaard'],
  'steps.example_photo': ['voorbeeldfoto'],
  'steps.intro_grip': ['intro_grip'],
  'steps.intro_finishing': ['intro_afwerking'],

  'fields.hand': ['veld_hand'],
  'fields.club': ['veld_club'],
  'fields.club_help': ['hulp_club'],
  'fields.flex': ['veld_flex'],
  'fields.weight': ['veld_gewicht'],
  'fields.flex_weight_help': ['hulp_shaft'],
  'fields.choose_flex_first': ['kies_flex_eerst'],
  'fields.head_brand': ['veld_adaptermerk'],
  'fields.generation': ['veld_adaptergeneratie'],
  'fields.length': ['veld_lengte'],
  'fields.tipping': ['veld_tipping'],
  'fields.grip_build_up': ['veld_griplaagdikte'],
  'fields.grip_logo': ['veld_griplogo'],
  'fields.spine_alignment': ['veld_spining'],
  'fields.more_info_link': ['uitklap'],
  'fields.length_note': ['letop_lengte'],
  'fields.head_note': ['letop_kop'],
  'fields.choose_club_first': ['kies_club_eerst'],
  'fields.follows_from_club': ['volgt_uit_club'],

  'options.club_driver': ['clubs', 'driver'],
  'options.club_fairway_3': ['clubs', 'fw3'],
  'options.club_fairway_5': ['clubs', 'fw5'],
  'options.club_fairway_7': ['clubs', 'fw7'],
  'options.club_hybrid_3': ['clubs', 'hy3'],
  'options.club_hybrid_4': ['clubs', 'hy4'],
  'options.club_hybrid_5': ['clubs', 'hy5'],
  'options.right_handed': ['hand_rechts'],
  'options.left_handed': ['hand_links'],
  'options.length_standard': ['lengte_standaard'],
  'options.length_finer_steps': ['lengte_fijn'],
  'options.length_half_steps': ['lengte_grof'],
  'options.no_tipping': ['tipping_geen'],
  'options.tipping_little_stiffer': ['tipping_iets'],
  'options.tipping_lot_stiffer': ['tipping_stuk'],
  'options.standard': ['standaard'],
  'options.build_up_1': ['griplaagdikte', 0],
  'options.build_up_2': ['griplaagdikte', 1],
  'options.build_up_3': ['griplaagdikte', 2],
  'options.build_up_4': ['griplaagdikte', 3],
  'options.build_up_5': ['griplaagdikte', 4],
  'options.logo_up': ['logo_boven'],
  'options.logo_down': ['logo_onder'],
  'options.no_spine_alignment': ['spining_niet'],
  'options.spine_alignment': ['spining_wel'],
  'options.spine_alignment_note': ['spining_sub'],
  'options.all_brands': ['alle_merken'],
  'options.all_sizes': ['alle_maten'],
  'options.size_undersize': ['maat_undersize'],
  'options.size_standard': ['maat_standaard'],
  'options.size_midsize': ['maat_midsize'],
  'options.size_jumbo': ['maat_jumbo'],
  'options.size_filter_label': ['maat_filter'],
  'options.grip_search_placeholder': ['grip_zoek'],
  'options.grip_search_label': ['grip_zoek_label'],
  'options.not_in_this_flex': ['reden_gewicht'],

  'summary.parts_sum': ['som_onderdelen'],
  'summary.shaft': ['sv_shaft'],
  'summary.adapter': ['sv_adapter'],
  'summary.grip': ['sv_grip'],
  'summary.length': ['sv_lengte'],
  'summary.tipping': ['sv_tipping'],
  'summary.grip_build_up': ['sv_griplaagdikte'],
  'summary.grip_logo': ['sv_griplogo'],
  'summary.spine_alignment': ['sv_spining'],
  'summary.delivery_time': ['sv_levertijd'],
  'summary.delivery_time_default': ['levertijd'],
  'summary.total': ['sv_totaal'],
  'summary.still_to_choose': ['sv_nog_kiezen'],
  'summary.none': ['sv_geen'],
  'summary.yes': ['ja'],
  'summary.no': ['nee'],
  'summary.complete': ['compleet'],
  'summary.missing_club': ['ontbreekt_club'],
  'summary.missing_shaft': ['ontbreekt_shaft'],
  'summary.missing_adapter': ['ontbreekt_adapter'],
  'summary.missing_grip': ['ontbreekt_grip'],
  'summary.add_to_cart_button': ['bouwknop'],
  'summary.add_to_cart_busy': ['bouwknop_bezig'],
  'summary.custom_made_note': ['maatwerknoot'],

  'errors.combination_blocked': ['reden_blokkade'],
  'errors.option_gone': ['fout_weg'],
  'errors.cannot_be_built': ['fout_onbouwbaar'],
  'errors.try_again': ['fout_storing'],
  'errors.shaft_page_link': ['fout_shaftlink'],
};

/* Zinnen met een waarde erin. Elke vorm (enkelvoud/meervoud, links/rechts, wood/hybride) is
   een eigen tekst met [plaatshouders]; `kies` wijst de vorm aan zoals de functie in de tabel
   dat doet, `waarden` levert de plaatshouders. De standaardtekst van een vorm is de functie
   zelf, aangeroepen met merkwaarden (zie `zinSjabloon`). */
const M = { a: 7301, b: 7302, c: 7303, tip: '7304', merk: 'Qmerkq', club: 'Qclubq', lijst: ['Qlijstq'], levertijd: 'Qlevertijdq' };
export const ZINNEN = {
  reden_grip_hand: {
    sleutel: 'errors.grips_hand_only',
    vormen: { one_left: [1, 'links'], one_right: [1, 'rechts'], other_left: [M.a, 'links'], other_right: [M.a, 'rechts'] },
    kies: (aantal, hand) => (aantal === 1 ? 'one' : 'other') + '_' + (hand === 'links' ? 'left' : 'right'),
    waarden: (aantal) => ({ count: aantal }),
  },
  reden_tipping_max: { sleutel: 'errors.tipping_max', vormen: { '': [M.a] }, kies: () => '', waarden: (max) => ({ max: maat(max) }) },
  reden_lengte_grens: { sleutel: 'errors.longer_than_uncut', vormen: { '': [M.a] }, kies: () => '', waarden: (l) => ({ uncut_length: maat(l) }) },
  stap_van: { sleutel: 'steps.step_of', vormen: { '': [M.a, M.b] }, kies: () => '', waarden: (n, totaal) => ({ step: n, total: totaal }) },
  intro_adapter: { sleutel: 'steps.intro_adapter', vormen: { '': [M.tip] }, kies: () => '', waarden: (tip) => ({ tip }) },
  laag_sub: {
    sleutel: 'options.extra_layers',
    vormen: { one: [1], other: [M.a] },
    kies: (n) => (n === 1 ? 'one' : 'other'),
    waarden: (n) => ({ count: n }),
  },
  waarom_club: {
    sleutel: 'fields.club_reason',
    vormen: { wood: [M.tip, false], hybrid: [M.tip, true] },
    kies: (tip, hybride) => (hybride ? 'hybrid' : 'wood'),
    waarden: (tip) => ({ tip }),
  },
  waarom_adapters: {
    sleutel: 'fields.adapters_reason',
    vormen: { one: [1, M.tip], other: [M.a, M.tip] },
    kies: (aantal) => (aantal === 1 ? 'one' : 'other'),
    waarden: (aantal, tip) => ({ count: aantal, tip }),
  },
  waarom_adapters_club: {
    sleutel: 'fields.adapters_club_reason',
    vormen: { one: [1, M.tip, M.club], other: [M.a, M.tip, M.club] },
    kies: (aantal) => (aantal === 1 ? 'one' : 'other'),
    waarden: (aantal, tip, club) => ({ count: aantal, tip, club, club_lowercase: String(club).toLowerCase() }),
  },
  waarom_generaties: {
    sleutel: 'fields.generations_reason',
    vormen: { one: [1, M.merk], other: [M.a, M.merk] },
    kies: (aantal) => (aantal === 1 ? 'one' : 'other'),
    waarden: (aantal, merk) => ({ count: aantal, brand: merk }),
  },
  waarom_grips: {
    sleutel: 'fields.grips_reason',
    vormen: { all: [M.b, M.b], some: [M.a, M.b] },
    kies: (getoond, totaal) => (getoond === totaal ? 'all' : 'some'),
    waarden: (getoond, totaal) => ({ total: totaal, shown: getoond }),
  },
  waarom_grips_cap: { sleutel: 'fields.grips_first_shown', vormen: { '': [M.a] }, kies: () => '', waarden: (cap) => ({ count: cap }) },
  generaties: {
    sleutel: 'fields.generations',
    vormen: { one: [1], other: [M.a] },
    kies: (n) => (n === 1 ? 'one' : 'other'),
    waarden: (n) => ({ count: n }),
  },
  ontbreekt: { sleutel: 'summary.still_to_choose_list', vormen: { '': [M.lijst] }, kies: () => '', waarden: (lijst) => ({ list: lijst.join(', ') }) },
  vertrouwen: { sleutel: 'summary.built_by_hand', vormen: { '': [M.levertijd] }, kies: () => '', waarden: (levertijd) => ({ delivery_time: levertijd }) },
};

const zinSleutel = (zin, vorm) => zin.sleutel + (vorm ? '_' + vorm : '');

/* De standaardtekst van één vorm, met [plaatshouders]: de functie aangeroepen met de
   merkwaarden, en elke merkwaarde vervangen door zijn naam. Een waarde van 1 (de
   enkelvoudsvorm) blijft staan: die hoort bij de zin. */
function zinSjabloon(tabel, naam, vorm) {
  const zin = ZINNEN[naam];
  const args = zin.vormen[vorm];
  const vorige = STRINGS;
  STRINGS = tabel; /* `maat` schrijft met het decimaalteken van de actieve taal */
  try {
    let tekst = tabel[naam](...args);
    for (const [plek, waarde] of Object.entries(zin.waarden(...args))) {
      if (String(waarde).length > 2) tekst = tekst.split(String(waarde)).join('[' + plek + ']');
    }
    return tekst;
  } finally {
    STRINGS = vorige;
  }
}

/** Alle standaardteksten van een taal als locale-sleutel → tekst (voor het vulscript en de tests). */
export function standaardTeksten(taal) {
  const tabel = TALEN[taal] || EN;
  const uit = {};
  for (const [sleutel, pad] of Object.entries(TEKSTEN)) uit[sleutel] = pad.reduce((o, k) => o[k], tabel);
  for (const [naam, zin] of Object.entries(ZINNEN)) {
    for (const vorm of Object.keys(zin.vormen)) uit[zinSleutel(zin, vorm)] = zinSjabloon(tabel, naam, vorm);
  }
  return uit;
}

/* Shopify's `t` escapet vertaalde tekst; de configurator escapet zelf bij het tekenen. */
const ontEscape = (tekst) =>
  String(tekst).replace(/&(amp|lt|gt|quot|#39|#x27);/g, (_, e) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'" })[e]);

/* [club_lowercase] telt als [club]: dezelfde waarde, alleen anders geschreven. */
const plaatsen = (tekst) => new Set((tekst.match(/\[[a-z_]+\]/g) || []).map((p) => (p === '[club_lowercase]' ? '[club]' : p)));

/**
 * De tabel van een taal met de teksten van de eigenaar erover. Een leeg veld blijft de
 * standaard; een zin die een plaatshouder van de standaard mist, ook — dan verdwijnt er
 * geen levertijd of aantal stilletjes.
 */
export function metTeksten(tabel, teksten) {
  const uit = { ...tabel, clubs: { ...tabel.clubs }, stap_titel: { ...tabel.stap_titel }, stap_kort: { ...tabel.stap_kort }, griplaagdikte: [...tabel.griplaagdikte] };
  const eigen = (sleutel) => {
    const waarde = teksten[sleutel];
    return typeof waarde === 'string' && waarde.trim() ? ontEscape(waarde.trim()) : null;
  };
  for (const [sleutel, pad] of Object.entries(TEKSTEN)) {
    const tekst = eigen(sleutel);
    if (tekst === null) continue;
    if (pad.length === 1) uit[pad[0]] = tekst;
    else uit[pad[0]][pad[1]] = tekst;
  }
  for (const [naam, zin] of Object.entries(ZINNEN)) {
    const standaard = tabel[naam];
    uit[naam] = (...args) => {
      const vorm = zin.kies(...args);
      const tekst = eigen(zinSleutel(zin, vorm));
      if (tekst === null) return standaard(...args);
      const nodig = plaatsen(zinSjabloon(tabel, naam, vorm));
      const heeft = plaatsen(tekst);
      for (const plek of nodig) if (!heeft.has(plek)) return standaard(...args);
      let zinTekst = tekst;
      for (const [plek, waarde] of Object.entries(zin.waarden(...args))) zinTekst = zinTekst.split('[' + plek + ']').join(String(waarde));
      return zinTekst;
    };
  }
  return uit;
}

/* De werkbon is voor de werkplaats en blijft Nederlands, welke taal de koper ook las
   (#86, beslissing 4). Alleen de worker leest dit. */
export const WERKBONTAAL = {
  hand: { rechts: 'rechts', links: 'links' },
  griplogo: { boven: 'boven', onder: 'onder' },
};

/* De schrijfwijze van een maat in inch: 0.5 → 0,5" — de worker schrijft de werkbontaal
   met deze twee, zodat er maar één notatie bestaat (spec #37 §F). Het scherm schrijft met
   het decimaalteken van de taal: `maat`. */
export function inch(waarde, decimaal = ',') {
  return String(waarde).replace('.', decimaal) + '"';
}

/** Een maat in inch zoals de koper hem leest: 0.5 → 0.5" in het Engels, 0,5" elders. */
export const maat = (waarde) => inch(waarde, STRINGS.decimaal);

/** De clubnaam in de taal van de koper; `CLUBS` draagt de Nederlandse voor de werkbon. */
export const clubNaam = (club) => (club ? STRINGS.clubs[club.id] || club.naam : null);

/* ------------------------------------------------------------------ CONFIG */
/* Club-as en Blokkeerlijst leven als config in code, niet in Shopify
   (docs/metafieldschema.md §C). */

/*  LET OP — PLAATSHOUDER.  De kolom `standaardtipping` bestaat vanaf dag één, maar de
    waarden moeten van de clubmaker komen (#25, delta 3). Tot dat gesprek staat hier de
    grove categoriewaarde uit het prototype: driver 0", fairway en hybride 0,5". Vervang
    ze per Club — niet per categorie — zodra de waarden er zijn.

    Geen ijzers meer (#55, #57): een wood-shaft (`.335`/`.350`) draagt driver en fairway,
    een hybride-shaft (`.370`) de hybrides. `soort` is wat een Adapter met `club_soort`
    aanwijst: `driver`, `fairway` of `driver-fairway` (#55 punt 4). */
export const CLUBS = [
  { id: 'driver', naam: 'Driver', standaardlengte: 45, categorie: 'wood', soort: 'driver', standaardtipping: 0 },
  { id: 'fw3', naam: 'Fairway 3', standaardlengte: 43, categorie: 'wood', soort: 'fairway', standaardtipping: 0.5 },
  { id: 'fw5', naam: 'Fairway 5', standaardlengte: 42.5, categorie: 'wood', soort: 'fairway', standaardtipping: 0.5 },
  { id: 'fw7', naam: 'Fairway 7', standaardlengte: 42, categorie: 'wood', soort: 'fairway', standaardtipping: 0.5 },
  { id: 'hy3', naam: 'Hybride 3', standaardlengte: 40.5, categorie: 'hybrid', soort: 'hybrid', standaardtipping: 0.5 },
  { id: 'hy4', naam: 'Hybride 4', standaardlengte: 40, categorie: 'hybrid', soort: 'hybrid', standaardtipping: 0.5 },
  { id: 'hy5', naam: 'Hybride 5', standaardlengte: 39.5, categorie: 'hybrid', soort: 'hybrid', standaardtipping: 0.5 },
];

/* Deelmatches over {shaft, grip, adapter, club}: alle opgegeven sleutels moeten kloppen
   om te blokkeren, en een blokkade wint altijd van de formule. Leeg bij launch.
   Een waarde is een variant-id of — voor shaft en grip — de producthandle uit de payload.
   Voorbeeld: { shaft: 'fujikura-ventus-blue', club: 'driver' } */
export const BLOKKEERLIJST = [];

export const LENGTES = [-1.5, -1.25, -1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1, 1.25, 1.5];
export const TIPPINGEN = [0, 0.5, 1, 1.5];
/* Het raster dat standaard in beeld staat; het volle raster zit achter één schakelaar. */
export const LENGTES_GROF = [-1, -0.5, 0, 0.5, 1];
export const GRIPLAAGDIKTES = [0, 1, 2, 3, 4];

/* ------------------------------------------------------------------ MOTOR */

const rond = (n) => Math.round(n * 100) / 100;

/** Een hybride-shaft draagt de hybride-Clubs, al het andere driver en fairway (#67 §9). */
export function clubsVoorShaft(payload) {
  const hybride = /hybrid/i.test(payload.shaft.club_type || '');
  return CLUBS.filter((c) => (hybride ? c.categorie === 'hybrid' : c.categorie === 'wood'));
}

const isHybrideshaft = (payload) => clubsVoorShaft(payload)[0].categorie === 'hybrid';

/**
 * Past deze Adapter op deze Club? `club_soort` is leeg (past op alles) of noemt de soort
 * — `driver-fairway` noemt er twee. Zonder Club is er nog niets om op te filteren.
 */
function pastOpClub(adapter, club) {
  if (!club || !adapter.club_soort) return true;
  return String(adapter.club_soort).split('-').includes(club.soort);
}

/** De voorgevulde tipping van een Club, begrensd door max_tipping van de Shaft. */
export function standaardtipping(payload, clubId) {
  const club = CLUBS.find((c) => c.id === clubId);
  if (!club) return 0;
  const max = maxTipping(payload);
  return max === null ? club.standaardtipping : Math.min(club.standaardtipping, max);
}

function maxTipping(payload) {
  const rauw = payload.shaft.max_tipping;
  /* Leeg = geen limiet (het veld is optioneel, #25 delta 1). */
  return rauw === null || rauw === undefined || rauw === '' ? null : Number(rauw);
}

/** De namen waaronder de Blokkeerlijst een keuze mag aanwijzen. */
function noemers(payload, keuzes, extra) {
  const k = { ...keuzes, ...extra };
  const grip = payload.grips.find((g) => g.id === k.grip);
  return {
    shaft: [k.shaft, k.shaft == null ? null : payload.shaft.handle],
    grip: [k.grip, grip ? grip.product : null],
    adapter: [k.adapter],
    club: [k.club],
  };
}

function geblokkeerd(payload, keuzes, extra) {
  const namen = noemers(payload, keuzes, extra);
  return BLOKKEERLIJST.some((regel) => {
    const sleutels = Object.keys(regel);
    return (
      sleutels.length > 0 &&
      sleutels.every((s) => (namen[s] || []).some((n) => n != null && n === regel[s]))
    );
  });
}

/**
 * De hele stand: welke lijsten, met welke reden, wat de lengte uitleest, wat het kost,
 * of het compleet is en wat er mis is.
 */
export function evalueer(payload, keuzes) {
  const shaft = payload.shaft;
  const club = CLUBS.find((c) => c.id === keuzes.club) || null;
  const max = maxTipping(payload);
  const ongesneden = Number(shaft.raw_length) || null; // 0 of leeg = geen grens

  const clubs = clubsVoorShaft(payload).map((c) => {
    const blok = geblokkeerd(payload, keuzes, { club: c.id });
    return { ...c, kiesbaar: !blok, reden: blok ? STRINGS.reden_blokkade : null };
  });

  const shaftVarianten = shaft.varianten.map((v) => merkKiesbaar(v, payload, keuzes, { shaft: v.id }));

  /* Adapter past op Shaft: gelijke tipdiameter — de Liquid-snippet filtert daar al op,
     de motor doet het nog eens omdat de worker dezelfde payload niet zelf samenstelt.
     Daarbovenop de Club: `club_soort` op de Adapter (#55 punt 4). Maakt de Club de lijst
     korter dan de tip alleen, dan zegt `adaptersReden` waarom. */
  const opTip = payload.adapters.filter((a) => a.tip_diameter === shaft.tip_diameter);
  const adapters = opTip
    .filter((a) => pastOpClub(a, club))
    .map((a) => merkKiesbaar(a, payload, keuzes, { adapter: a.id }));
  const adaptersReden =
    adapters.length < opTip.length
      ? STRINGS.waarom_adapters_club(adapters.length, shaft.tip_diameter, clubNaam(club))
      : null;

  /* Hand filtert grips: een gevulde hand-side die de gekozen Hand uitsluit haalt de grip
     uit de lijst. Een lege hand-side geldt voor beide handen. Core size en buttdiameter
     zijn informatief en geen regel meer (#55 punt 2). */
  const anderehand = keuzes.hand === 'links' ? 'rechts' : 'links';
  const passendeGrips = payload.grips.filter((g) => !sluitUit(g.hand, keuzes.hand));
  const gripsWeg = payload.grips.length - passendeGrips.length;
  const grips = passendeGrips.map((g) => merkKiesbaar(g, payload, keuzes, { grip: g.id }));

  const tipping = TIPPINGEN.map((waarde) => ({
    waarde,
    kiesbaar: max === null || waarde <= max,
    reden: max !== null && waarde > max ? STRINGS.reden_tipping_max(max) : null,
  }));

  const lengtes = LENGTES.map((waarde) => {
    const uitkomst = club ? rond(club.standaardlengte + waarde) : null;
    const telang = ongesneden !== null && uitkomst !== null && uitkomst > ongesneden;
    return {
      waarde,
      uitkomst,
      kiesbaar: !telang,
      reden: telang ? STRINGS.reden_lengte_grens(ongesneden) : null,
    };
  });

  const uitlezing = {
    standaard: club ? club.standaardlengte : null,
    afwijking: keuzes.lengte,
    uitkomst: club ? rond(club.standaardlengte + keuzes.lengte) : null,
  };

  const gekozen = [
    shaft.varianten.find((v) => v.id === keuzes.shaft),
    payload.adapters.find((a) => a.id === keuzes.adapter),
    payload.grips.find((g) => g.id === keuzes.grip),
  ];
  const prijs = gekozen.reduce((som, item) => som + (item ? item.prijs : 0), 0);

  /* Een keuze die uit de payload verdween, of die niet (meer) kiesbaar is, is een fout —
     anders zou de bouwknop aan staan op een geblokkeerde component. */
  const fouten = [];
  for (const sleutel of ['shaft', 'adapter', 'grip']) {
    if (keuzes[sleutel] == null) continue;
    const lijst = { shaft: shaftVarianten, adapter: adapters, grip: grips }[sleutel];
    const item = lijst.find((x) => x.id === keuzes[sleutel]);
    if (!item) fouten.push(STRINGS.fout_weg);
    else if (!item.kiesbaar) fouten.push(item.reden || STRINGS.fout_weg);
  }
  if (geblokkeerd(payload, keuzes, {})) fouten.push(STRINGS.reden_blokkade);

  const compleet =
    !!club && !!keuzes.shaft && !!keuzes.grip && !!keuzes.adapter && fouten.length === 0;

  return {
    clubs,
    shaftVarianten,
    adapters,
    adaptersReden,
    grips,
    gripsReden: gripsWeg > 0 ? STRINGS.reden_grip_hand(gripsWeg, anderehand) : null,
    tipping,
    lengtes,
    uitlezing,
    prijs,
    valuta: payload.valuta,
    compleet,
    fouten: [...new Set(fouten)],
  };
}

/** De Blokkeerlijst — de ene regel die elke lijst deelt. Voorraad is geen regel (#57). */
function merkKiesbaar(item, payload, keuzes, extra) {
  const blok = geblokkeerd(payload, keuzes, extra);
  return { ...item, kiesbaar: !blok, reden: blok ? STRINGS.reden_blokkade : null };
}

/** Een gevulde hand-side die de gekozen hand niet noemt, sluit uit. */
function sluitUit(handSide, hand) {
  if (!handSide) return false;
  const tekst = String(handSide).toLowerCase();
  const noemt = (h) => tekst.includes(h === 'links' ? 'link' : 'recht') || tekst.includes(h === 'links' ? 'left' : 'right');
  if (!noemt('links') && !noemt('rechts')) return false;
  return !noemt(hand);
}

/* --------------------------------------------------------------- MUTATIES */

export function standaardKeuzes(payload) {
  return {
    hand: 'rechts',
    club: null,
    shaft: null,
    adapter: null,
    grip: null,
    lengte: 0,
    tipping: 0,
    griplaagdikte: 0,
    griplogo: 'boven',
    spining: false,
  };
}

/**
 * Eén keuze zetten, met de mutatieregels erachteraan: Club wisselen wist de Adapter en
 * herberekent de tipping, en elke keuze die na de mutatie niet meer in zijn lijst staat
 * valt weg. Wat nog geldig is, blijft staan. Pure functie: `keuzes` gaat ongemoeid terug.
 */
export function muteer(payload, keuzes, sleutel, waarde) {
  const volgend = { ...keuzes, [sleutel]: waarde };

  if (sleutel === 'club') {
    volgend.adapter = null;
    volgend.tipping = standaardtipping(payload, waarde);
  }

  const na = evalueer(payload, volgend);
  if (volgend.adapter != null && !na.adapters.some((a) => a.id === volgend.adapter)) {
    volgend.adapter = null;
  }
  if (volgend.grip != null && !na.grips.some((g) => g.id === volgend.grip)) {
    volgend.grip = null;
  }
  if (volgend.shaft != null && !na.shaftVarianten.some((v) => v.id === volgend.shaft)) {
    volgend.shaft = null;
  }
  return volgend;
}

/* ------------------------------------------------------------ QUERY-STRING */
/* De keuzes reizen in de query (docs/paginastructuur.md §3, spec #37 §D). Spec §D schrijft
   `flex=` voor de shaft; een Shaftvariant is flex × gewicht, dus een flex alleen wijst er
   geen aan — vandaar `shaft=` met het variant-id, gelijk aan de sleutel in `keuzes`. */

const QUERYSLEUTELS = {
  hand: 'hand',
  club: 'club',
  shaft: 'shaft',
  adapter: 'adapter',
  grip: 'grip',
  lengte: 'len',
  tipping: 'tip',
  griplaagdikte: 'laag',
  griplogo: 'logo',
  spining: 'spin',
};

/* De wizardstap (1–5) reist als `?stap=` naast de keuzes (spec #67 §9; sleuteltabel in
   docs/paginastructuur.md §3). Mijn specs (`assets/specs.js`) leest dezelfde sleutel. De
   stap is een weergavekeuze en geen keuze: `naarQuery` schrijft hem alleen als hij wordt
   meegegeven, zodat de opgeslagen spec-URL hem niet draagt. */
export const STAPSLEUTEL = 'stap';
export const STAPPEN = 5;

export function naarQuery(keuzes, stap) {
  const query = new URLSearchParams();
  for (const [sleutel, param] of Object.entries(QUERYSLEUTELS)) {
    const waarde = keuzes[sleutel];
    if (waarde === null || waarde === undefined || waarde === '') continue;
    query.set(param, sleutel === 'spining' ? (waarde ? '1' : '0') : String(waarde));
  }
  if (stap !== undefined && stap !== null) query.set(STAPSLEUTEL, String(stap));
  return query.toString();
}

/** De stap uit de URL: een heel getal 1–5, alles anders (ook niets) is stap 1. */
export function stapUitQuery(querystring) {
  const rauw = new URLSearchParams(querystring || '').get(STAPSLEUTEL);
  const stap = Number(rauw);
  return rauw !== null && Number.isInteger(stap) && stap >= 1 && stap <= STAPPEN ? stap : 1;
}

export function uitQuery(payload, querystring) {
  const query = new URLSearchParams(querystring || '');
  const keuzes = standaardKeuzes(payload);
  const lees = (sleutel) => query.get(QUERYSLEUTELS[sleutel]);

  if (lees('hand') === 'links' || lees('hand') === 'rechts') keuzes.hand = lees('hand');

  const club = clubsVoorShaft(payload).find((c) => c.id === lees('club'));
  if (club) {
    keuzes.club = club.id;
    keuzes.tipping = standaardtipping(payload, club.id);
  }

  const id = (sleutel, lijst) => {
    const waarde = Number(lees(sleutel));
    return lijst.some((x) => x.id === waarde) ? waarde : null;
  };
  keuzes.shaft = id('shaft', payload.shaft.varianten);
  keuzes.adapter = id('adapter', payload.adapters);
  keuzes.grip = id('grip', payload.grips);

  /* `Number(null)` is 0 en 0 staat in elke lijst — daarom eerst kijken of de parameter
     er überhaupt stond, anders overschrijft een lege query de voorgevulde tipping. */
  const getal = (sleutel, lijst) => {
    const rauw = lees(sleutel);
    if (rauw === null) return null;
    const waarde = Number(rauw);
    return lijst.includes(waarde) ? waarde : null;
  };

  const lengte = getal('lengte', LENGTES);
  if (lengte !== null) keuzes.lengte = lengte;

  const tipping = getal('tipping', TIPPINGEN);
  if (tipping !== null) keuzes.tipping = tipping;

  const laag = getal('griplaagdikte', GRIPLAAGDIKTES);
  if (laag !== null) keuzes.griplaagdikte = laag;

  if (lees('griplogo') === 'onder' || lees('griplogo') === 'boven') {
    keuzes.griplogo = lees('griplogo');
  }
  if (lees('spining') !== null) keuzes.spining = lees('spining') === '1';

  return keuzes;
}

/**
 * `?adapter=` draagt normaal het variant-id van één adapter. `/pages/adapters` zet er een
 * **merknaam** in, zodat één link vanaf die pagina op elke shaftpagina werkt — welke
 * generatie past hangt van de tip af en dat weet de pagina niet. Geen nieuwe querysleutel
 * (N18): een niet-numerieke waarde is een merk, en zodra de koper zelf kiest schrijft
 * `naarQuery` er weer een id in.
 *
 * @returns {string|null} het merk zoals het in de payload staat, of null.
 */
export function merkUitQuery(payload, querystring) {
  const rauw = new URLSearchParams(querystring || '').get(QUERYSLEUTELS.adapter);
  if (!rauw || Number.isFinite(Number(rauw))) return null;
  const gezocht = rauw.trim().toLowerCase();
  const treffer = (payload.adapters || []).find((a) => String(a.merk || '').toLowerCase() === gezocht);
  return treffer ? treffer.merk : null;
}

/* -------------------------------------------------------------- KOOPAANROEP */
/* De koopweg zit in het component (N19), maar de beslissingen erin zijn pure functies:
   welke permalink, welke melding, welke voorraad ververst. Zo is de hele koopweg in
   Node te toetsen zonder browser (spec #37, Testing Decisions). */

/** Het kale variantnummer uit een gid of getal; `/cart/add.js` wil een getal. */
function variantNummer(variantId) {
  const nummer = String(variantId ?? '').match(/(\d+)\s*$/);
  return nummer ? Number(nummer[1]) : null;
}

/** De wortel van de shop uit `shaftUrl`, zodat een markt-prefix meegaat (N17). */
function karWortel(payload) {
  return String(payload.shaftUrl || '').split('/products/')[0];
}

/**
 * `/cart/<variant>:1?storefront=true`. Zonder `storefront=true` stuurt Shopify een kar-permalink
 * rechtstreeks naar de checkout (gemeten 13 sep 2026, acceptatierun #76) en slaat hij de
 * karpagina over — en dáár schrijft `kar.js` de werkbon-attributen en dwingt de
 * maatwerkcheckbox af. Let op: een permalink **vervangt** de karinhoud (gemeten 15 sep 2026,
 * #90), dus thuis is dit niet de route — zie `koopweg`.
 */
export function karPermalink(payload, variantId) {
  const nummer = variantNummer(variantId);
  if (nummer == null) return null;
  return karWortel(payload) + '/cart/' + nummer + ':1?storefront=true';
}

/**
 * Wat er na de koopaanroep gebeurt. Statussen en teksten uit
 * `docs/prijs-en-voorraadcontract.md` §4: 422 = geval 8, de rest = geval 7. Een 409
 * bestaat niet meer: voorraad is geen poort (#57). `status: 0` is een netwerkfout en
 * valt bewust in de storingsbak. Bij 200 komen alle bestemmingen mee; `koopweg` kiest.
 */
export function koopgevolg(payload, status, antwoord) {
  const niets = { variant: null, toevoegen: null, kar: null, permalink: null };
  if (status === 200) {
    const variant = variantNummer(antwoord && antwoord.variantId);
    if (variant == null) return { ...niets, melding: STRINGS.fout_storing };
    const wortel = karWortel(payload);
    return {
      variant,
      toevoegen: wortel + '/cart/add.js',
      kar: wortel + '/cart',
      permalink: karPermalink(payload, variant),
      melding: null,
    };
  }
  if (status === 422) return { ...niets, melding: STRINGS.fout_onbouwbaar };
  return { ...niets, melding: STRINGS.fout_storing };
}

/**
 * Welke landing de build krijgt (#90). Thuis — de kar-URL op hetzelfde origin als de pagina —
 * gaat hij via `POST /cart/add.js` naar `/cart`, zodat een tweede build náást de eerste komt.
 * Op een vreemd origin (de web component bij Golf Direction, #32) zijn de cookies van de shop
 * er niet en blijft de permalink over; die vervangt de kar, en dat is daar het beste dat kan.
 */
export function koopweg(gevolg, paginaUrl) {
  if (gevolg.variant == null) return { toevoegen: null, kar: null, body: null, permalink: null };
  const thuis = new URL(gevolg.kar, paginaUrl).origin === new URL(paginaUrl).origin;
  if (!thuis) return { toevoegen: null, kar: null, body: null, permalink: gevolg.permalink };
  return {
    toevoegen: gevolg.toevoegen,
    kar: gevolg.kar,
    body: { items: [{ id: gevolg.variant, quantity: 1 }] },
    permalink: null,
  };
}

/**
 * Welke keuzes uit de URL niet meer in de payload staan. Een gedeelde link opent
 * voorgevuld met de rest, en dit is de reden die erbij hoort (`paginastructuur.md` §3).
 */
export function verdwenen(payload, querystring) {
  const query = new URLSearchParams(querystring || '');
  const keuzes = uitQuery(payload, querystring);
  return ['club', 'shaft', 'adapter', 'grip'].filter(
    (sleutel) => query.get(QUERYSLEUTELS[sleutel]) && keuzes[sleutel] == null
  );
}

/* ----------------------------------------------------------------- WIZARD */
/* De vijf stappen (spec #67 §9): 1 Club, 2 Flex en gewicht, 3 Grip, 4 Adapter, 5 Afwerking
   met het overzicht. "Volgende" staat pas aan als de stap compleet is; wat dat per stap is,
   staat hier en nergens anders. */

const STAPVELDEN = { 1: 'club', 2: 'shaft', 3: 'grip', 4: 'adapter' };

/**
 * Is deze stap compleet? Stappen 1–4 zodra hun eigen keuze staat én nog in de lijst
 * staat (een weggevallen of geblokkeerde keuze telt niet); Hand heeft een standaard en
 * telt dus niet mee. Stap 5 is compleet als de build het is.
 */
export function stapCompleet(payload, keuzes, stap) {
  const uit = evalueer(payload, keuzes);
  if (stap === STAPPEN) return uit.compleet;
  const sleutel = STAPVELDEN[stap];
  if (!sleutel || keuzes[sleutel] == null) return false;
  const lijst = { club: uit.clubs, shaft: uit.shaftVarianten, grip: uit.grips, adapter: uit.adapters }[sleutel];
  const item = lijst.find((x) => x.id === keuzes[sleutel]);
  return !!item && item.kiesbaar;
}

/* Dezelfde foto op een andere breedte. De payload draagt een kleine `image_url` (96 breed,
   voor de chip); de Shopify-CDN schaalt op de `width`-parameter, precies zoals `image_tag`
   zijn srcset bouwt. Een thema-asset (de plaatshouder) heeft die parameter niet en blijft
   zoals hij is. */
export const opMaat = (url, breedte) => String(url || '').replace(/([?&]width=)\d+/, '$1' + breedte);

/* De vlakfoto als srcset: breed staat het vlak op ±400px, smal tot 440px. Een 1x-scherm haalt
   zo 400 breed (±60 kB) in plaats van 800 (±240 kB bij de telefoonfoto's van de eigenaar).
   Een thema-asset schaalt niet mee en krijgt geen srcset. */
const VLAKMATEN = [400, 600, 800];
const VLAKSIZES = '(min-width: 860px) min(28vw, 400px), min(92vw, 440px)';
export const vlakSrcset = (url) =>
  /[?&]width=\d+/.test(url) ? VLAKMATEN.map((b) => opMaat(url, b) + ' ' + b + 'w').join(', ') : '';

/**
 * Het label van een bewaarde spec: club · shaft · flex · gewicht · lengte · adapter
 * (docs/paginastructuur.md §3). De leeslaag op /account splitst op ` · ` en neemt het
 * eerste deel als club; wat ontbreekt valt weg.
 */
export function speclabel(payload, keuzes) {
  const club = CLUBS.find((c) => c.id === keuzes.club);
  const variant = payload.shaft.varianten.find((v) => v.id === keuzes.shaft);
  const adapter = payload.adapters.find((a) => a.id === keuzes.adapter);
  const lengte = club ? maat(rond(club.standaardlengte + keuzes.lengte)) : null;
  return [
    clubNaam(club),
    payload.shaft.titel,
    variant && variant.flex,
    variant && variant.gewicht,
    lengte,
    adapter && [adapter.merk, adapter.generatie].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * De lijst voor `localStorage['shaftlab.specs']` met deze spec bovenaan. `bestaand` is de
 * rauwe opgeslagen tekst (of al een lijst); onleesbaar telt als leeg. Pure functie —
 * lezen en schrijven doet het element. Vorm: contract in docs/paginastructuur.md §3.
 */
export function bewaarSpec(bestaand, payload, keuzes) {
  let lijst = bestaand;
  if (typeof lijst === 'string') {
    try {
      lijst = JSON.parse(lijst);
    } catch {
      lijst = [];
    }
  }
  if (!Array.isArray(lijst)) lijst = [];
  const spec = {
    url: payload.shaftUrl + '?' + naarQuery(keuzes),
    label: speclabel(payload, keuzes),
    bewaard: new Date().toISOString(),
  };
  return [spec, ...lijst];
}

/* ------------------------------------------------------------------- OPMAAK */

/* Prijs volgt de valuta van de markt (N3); centen in, tekst uit. */
function eur(centen, valuta) {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: valuta || 'EUR' }).format(
    (centen || 0) / 100
  );
}

const esc = (waarde) =>
  String(waarde == null ? '' : waarde).replace(
    /[&<>"]/g,
    (teken) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[teken]
  );

/* Een afwijking met teken: 0.5 → +0,5" en −0,5" */
export const afwijking = (v, decimaal = ',') => (v > 0 ? '+' : v < 0 ? '−' : '') + inch(Math.abs(v), decimaal);


/* ---------------------------------------------------------------------- CSS */
/* De CSS staat als <style> in de shadow root (N7); tokens heten --sl-… en dragen elk een
   harde fallback (N8) met Rick's waarde (DESIGN.md v4), zodat het component zelfdragend is
   op een gastpagina. Nooit `position: fixed` (N9): de onderbalk is sticky binnen de eigen
   box. Maten staan als Rick's tokens: --t-ui 14, --t-body 16, --t-small 13, --t-label 12,
   --t-h3 18, --t-price 28; hoek 2px op bediening, 0 elders, geen schaduw. */

const CSS = `
:host{display:block;container-type:inline-size;
  font-family:var(--sl-sans,"Inter","Helvetica Neue",Arial,sans-serif);
  font-size:16px;line-height:1.55;color:var(--sl-tekst,#111111);-webkit-text-size-adjust:100%;
  font-variant-numeric:tabular-nums}
*,*::before,*::after{box-sizing:border-box}
button,input{font:inherit;color:inherit;margin:0}
button{cursor:pointer;background:none;border:0;padding:0}
:focus-visible{outline:2px solid var(--sl-blauw,#0f2593);outline-offset:2px}
.stil{color:var(--sl-tekst-2,#6b6f7a);margin:0}
.vh{position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap}

/* De stap: kop als tekst (geen stepper), titel in --t-h3, intro in --t-body */
.wizard{display:flex;flex-direction:column;gap:8px;padding-bottom:24px}
/* Ritme: stapkop strak op de voortgang (8), voortgang ruim van de titel (32), titel strak op
   de intro (16), intro ruim van de opties (24) — niet vier keer dezelfde 24. */
.stapkop{margin:0;font-size:13px;line-height:1.4;color:var(--sl-blauw,#0f2593)}
/* Breed zegt de voortgang welke stap het is, en de onderbalk zegt het nog eens: de stapkop
   staat alleen smal, boven de vijf balkjes. */
@container (min-width:721px){.stapkop{display:none}}

/* Voortgang (canvas 3c): de vijf stappen als tekst op een lijn, de huidige met een streep
   in --brand, de gemaakte keuze erachter in --ink-2. Geen knoppen: geen stepper. Smal
   worden het vijf balkjes van 2px, zoals het 390-artboard. */
.voortgang{display:flex;gap:0 20px;margin:0 0 24px;padding:0;list-style:none;
  border-bottom:1px solid var(--sl-lijn,#e2e2e0);font-size:14px;line-height:1.4;font-weight:500}
.voortgang li{position:relative;display:flex;gap:6px;padding:12px 0;
  color:var(--sl-tekst-2,#6b6f7a);white-space:nowrap}
.voortgang li.gedaan{color:var(--sl-tekst,#111111)}
.voortgang li[aria-current]{color:var(--sl-tekst,#111111)}
.voortgang li[aria-current]::after{content:"";position:absolute;left:0;right:0;bottom:-1px;height:2px;
  background:var(--sl-blauw,#0f2593);transform-origin:left}
.voortgang .vk{color:var(--sl-tekst-2,#6b6f7a);font-weight:400}
@container (max-width:720px){
  .voortgang{gap:4px;border-bottom:0}
  .voortgang li{flex:1;height:2px;padding:0;margin:0;border:0;background:var(--sl-lijn,#e2e2e0);overflow:hidden;color:transparent;font-size:0}
  .voortgang li.gedaan,.voortgang li[aria-current]{background:var(--sl-blauw,#0f2593)}
  .voortgang li::after{content:none}
}

/* Beweging (DESIGN.md §7: 150ms staat, 300ms lay-out, ease-out; Rick's \`rise\` uit het canvas).
   Elke render bouwt de DOM opnieuw, dus een transition vuurt nooit; wat nieuw is krijgt in
   render() de klasse .in en speelt één keer — de stap rijst op, de streep groeit, de gekozen
   chip en de vrijgekomen knop faden naar hun staat, de prijs wisselt (telt niet op). */
@keyframes rise{from{opacity:0;transform:translateY(6px)}}
@keyframes fade{from{opacity:0}}
@keyframes streep{from{transform:scaleX(0)}}
@keyframes kies{from{background:var(--sl-wit,#fff);border-color:var(--sl-lijn-2,#e2e2e0);color:var(--sl-tekst,#111111)}}
@keyframes aan{from{background:var(--sl-lijn,#e2e2e0);border-color:var(--sl-lijn,#e2e2e0);color:var(--sl-tekst-2,#6b6f7a)}}
.in{animation:rise 300ms ease-out both}
.intro.in{animation-delay:60ms}
.stap.in{animation-delay:120ms}
.voortgang li.in::after{animation:streep 300ms ease-out}
.voortgang li.in{animation:none}
.chip.in{animation:kies 300ms ease-out}
.knop.in{animation:aan 300ms ease-out}
.bedrag.in{animation:fade 150ms ease-out}
@container (max-width:720px){.voortgang li.in{animation:streep 300ms ease-out;transform-origin:left}}
/* Indrukken: chips 3%, knoppen 2% (Rick, 26 sep 2026: overal gelijk); onder reduced motion alleen de kleur */
@media (prefers-reduced-motion:no-preference){.chip:active:not(:disabled){transform:scale(.97)}.knop:active:not(:disabled){transform:scale(.98)}}
@media (prefers-reduced-motion:reduce){*,*::after{animation:none!important;transition:none!important}}
/* De vraag van de stap is de stem van de configurator: displaysnede op Rick's mobiele
   h2 (26px, canvas 3c 390), op elke breedte — onder de sectiekop van 32, boven de body. */
.staptitel{margin:0 0 8px;font-family:var(--sl-display,"Inter Tight","Helvetica Neue",Arial,sans-serif);
  font-size:26px;line-height:1.15;font-weight:600;letter-spacing:-.02em;text-wrap:balance}
.staptitel:focus{outline:0}
.intro{margin:0 0 16px;font-size:16px;line-height:1.55;color:var(--sl-tekst-2,#6b6f7a);max-width:62ch}
.stap{display:flex;flex-direction:column;gap:24px}

/* Stapvlak (canvas 3c): links het onderdeel van de stap, rechts de stap. Een foto staat op
   wit en past in het vlak (contain), de AI-plaatshouder vult --panel met de tag linksboven
   (DESIGN.md §5 en "Wat niet meebuigt"); naam en spec eronder zoals op een productvlak.
   Smal staat het vlak boven de stap in Rick's 350:220, breed sticky naast de opties, zodat
   de gekozen grip in beeld blijft terwijl je door de tegels scrolt. Breed is het vierkant,
   niet de 4:5 van de canvas: de foto's van de eigenaar zijn vierkant en staan op wit, dus
   een 4:5-vlak is onzichtbaar en zet het bijschrift los onder lege ruimte. */
.vlak{margin:0 0 24px;max-width:440px;display:flex;flex-direction:column;gap:12px}
.vlak-beeld{position:relative;aspect-ratio:350/220;overflow:hidden;background:var(--sl-wit,#fff)}
.vlak-beeld img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain}
.vlak-beeld .vlak-voor{filter:blur(6px)}
.vlak--voorbeeld .vlak-beeld{background:var(--sl-grond,#f2f2f0)}
.vlak--voorbeeld .vlak-beeld img{object-fit:cover}
.vlak .tag{position:absolute;left:8px;top:8px;padding:4px 6px;border-radius:var(--sl-r,2px);
  background:var(--sl-wit,#fff);color:var(--sl-tekst,#111111);font-size:11px;font-weight:500;line-height:1}
.vlak figcaption{display:flex;flex-direction:column;gap:2px}
.vlak-naam{font-size:18px;line-height:1.3;font-weight:600}
.vlak-spec{font-size:13px;line-height:1.45;color:var(--sl-tekst-2,#6b6f7a)}
.vlak.in{animation:fade 300ms ease-out both}
@container (min-width:860px){
  .stapraster--vlak{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,2fr);gap:48px;align-items:start}
  /* --sl-kop: hoogte van een sticky kop van de gastpagina die over het vlak kan hangen; zonder 0 */
  .vlak{position:sticky;top:calc(var(--sl-kop,0px) + 24px);transition:top 340ms cubic-bezier(.22,.61,.36,1);margin:0;max-width:none}
  .vlak-beeld{aspect-ratio:1/1}
}

/* Veld: label boven een rij opties (Rick: "Flex", "Gewicht", "Merk van je kop") */
.veld{display:flex;flex-direction:column;gap:8px}
.vlabel{font-size:12px;line-height:1.4;color:var(--sl-tekst-2,#6b6f7a)}

/* Afwerkingsrij: uitklap met de waarde in de kop, 48px, "+" rechts, 1px lijn onder */
.afw{border-bottom:1px solid var(--sl-lijn,#e2e2e0)}
.afw:first-of-type{border-top:1px solid var(--sl-lijn,#e2e2e0)}
.afw summary{display:flex;align-items:center;justify-content:space-between;gap:16px;min-height:48px;
  padding:8px 0;cursor:pointer;list-style:none;font-size:14px;font-weight:500;line-height:1.4}
.afw summary::-webkit-details-marker{display:none}
.afw summary:hover{color:var(--sl-blauw,#0f2593)}
.afw .aw{color:var(--sl-tekst-2,#6b6f7a);font-weight:400}
.afw .plus{flex:none;display:inline-flex;transition:transform 340ms cubic-bezier(.22,.61,.36,1)}
.afw[open]:not([data-klap="dicht"]) .plus{transform:rotate(45deg)}
.afw .binnen{padding:4px 0 16px}

/* Schakelaar naar het fijne lengteraster; tekstlink in --ink, onderstreept */
.meer{display:inline-flex;align-items:center;gap:6px;min-height:44px;align-self:flex-start;
  color:var(--sl-tekst,#111111);font-size:14px;font-weight:500;
  text-decoration:underline;text-underline-offset:3px}
.meer:hover{color:var(--sl-blauw,#0f2593)}

/* Opties: 44px, 1px --line, 2px hoek; actief 1,5px --brand op --brand-tint; spec in --ink-2,
   in een actieve optie in --ink (vloer: contrast 4,5:1) */
.chips{display:flex;flex-wrap:wrap;gap:8px}
.chip{position:relative;min-height:44px;padding:8px 14px;
  border:1px solid var(--sl-lijn,#e2e2e0);border-radius:var(--sl-r,2px);background:var(--sl-wit,#fff);
  font-size:14px;font-weight:500;color:var(--sl-tekst,#111111);text-align:left;line-height:1.4;
  transition:border-color var(--sl-wissel,150ms ease-out),background var(--sl-wissel,150ms ease-out),transform var(--sl-wissel,150ms ease-out)}
.chip:hover:not(:disabled){border-color:var(--sl-tekst,#111111)}
.chip .sub{display:block;font-weight:400;font-size:13px;color:var(--sl-tekst-2,#6b6f7a)}
.chip[aria-pressed="true"]{border:1.5px solid var(--sl-blauw,#0f2593);padding:7.5px 13.5px;
  background:var(--sl-blauw-tint,#e9ecf7);color:var(--sl-tekst,#111111)}
.chip[aria-pressed="true"] .sub{color:var(--sl-tekst,#111111)}
.chip:disabled{color:var(--sl-tekst-2,#6b6f7a);cursor:not-allowed;font-weight:400}
.chip--breed{flex:1 1 280px}
@container (min-width:900px){.chip--breed{flex-basis:220px;max-width:calc(33.33% - 6px)}}
/* Chip met productfoto (#93): de foto links in een vast vlak op --ground, geen rand of
   schaduw; ook op een actieve chip blijft het vlak wit. Uit = de foto gedimd, reden eronder. */
.chip--foto{display:flex;align-items:center;gap:12px}
.chip--foto>span{min-width:0}
.duim{flex:none;display:block;width:40px;height:40px;object-fit:contain;background:var(--sl-wit,#fff)}
.chip:disabled .duim{opacity:.45}
.zoekveld{width:100%;max-width:440px;min-height:48px;padding:0 16px;
  border:1px solid var(--sl-lijn,#e2e2e0);border-radius:var(--sl-r,2px);background:var(--sl-wit,#fff);
  font-size:14px;margin-bottom:12px}
.zoekveld::placeholder{color:var(--sl-tekst-2,#6b6f7a)}
.zoekveld:focus-visible{border-color:var(--sl-blauw,#0f2593)}

/* Waaromregel (--t-small, --ink-2) en waarschuwing (Rick: tekst in --warn, lijn erboven,
   geen vlak, geen icoon; direct bij de keuze waar hij over gaat) */
.waarom{margin:12px 0 0;font-size:13px;line-height:1.45;color:var(--sl-tekst-2,#6b6f7a);max-width:62ch}
.letop{margin:12px 0 0;padding-top:12px;border-top:1px solid var(--sl-lijn,#e2e2e0);
  font-size:13px;line-height:1.45;color:var(--sl-warn,#8a4b00);max-width:62ch}

/* Uitklap "Wat is dit?" — native <details>; tekstlink in --ink */
.uitklap{max-width:62ch}
.uitklap summary{display:inline-flex;align-items:center;gap:8px;min-height:44px;
  color:var(--sl-tekst,#111111);cursor:pointer;list-style:none;font-size:14px;font-weight:500;
  text-decoration:underline;text-underline-offset:3px}
.uitklap summary:hover{color:var(--sl-blauw,#0f2593)}
.uitklap summary::-webkit-details-marker{display:none}
.uitklap .min{display:none}
.uitklap[open]:not([data-klap="dicht"]) .plus{display:none}
.uitklap[open]:not([data-klap="dicht"]) .min{display:block}
.uitklap p{margin:8px 0 0;font-size:16px;line-height:1.55;color:var(--sl-tekst,#111111);max-width:62ch}

/* Lengte-uitlezing: standaard + afwijking = uitkomst */
.uitlezing{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;font-size:16px}
.uitlezing .zacht{color:var(--sl-tekst-2,#6b6f7a);font-size:13px}
.uitlezing .uit{font-weight:600;font-size:18px}

/* Overzicht op de laatste stap: regels naam links / prijs rechts, bewerkingen "—",
   totaal in --t-price met "som van de onderdelen" eronder */
.overzicht{border-top:1px solid var(--sl-tekst,#111111);padding-top:16px;max-width:560px}
.overzicht h3{margin:0 0 8px;font-size:18px;line-height:1.3;font-weight:600}
.regel{display:flex;justify-content:space-between;gap:16px;min-height:44px;align-items:center;
  border-bottom:1px solid var(--sl-lijn,#e2e2e0);font-size:14px;line-height:1.4}
.regel .l{color:var(--sl-tekst-2,#6b6f7a);flex:none}
.regel .w{text-align:right;font-weight:500}
.regel .w.leeg{font-weight:400;color:var(--sl-tekst-2,#6b6f7a)}
.regel .p{flex:none;margin-left:16px;color:var(--sl-tekst-2,#6b6f7a);font-weight:400}
.totaal{display:flex;justify-content:space-between;align-items:baseline;gap:16px;padding-top:16px}
.totaal .l{font-size:14px;font-weight:500}
.totaal .w{display:flex;flex-direction:column;align-items:flex-end}
.bedrag{font-family:var(--sl-display,"Inter Tight","Helvetica Neue",Arial,sans-serif);
  font-size:28px;line-height:1.1;font-weight:600;letter-spacing:-.02em}
.noot{font-size:12px;line-height:1.4;color:var(--sl-tekst-2,#6b6f7a)}
.acties{display:flex;align-items:center;gap:16px;flex-wrap:wrap;margin-top:16px}
.acties .stil{font-size:13px}

/* Knoppen: primair --brand wit 48px; secundair wit met --ink rand */
.knop{display:inline-flex;align-items:center;justify-content:center;min-height:48px;padding:0 24px;
  border:1px solid var(--sl-blauw,#0f2593);border-radius:var(--sl-r,2px);background:var(--sl-blauw,#0f2593);
  color:var(--sl-wit,#fff);font-size:14px;font-weight:500;line-height:1.4;white-space:nowrap;
  transition:background var(--sl-wissel,150ms ease-out),border-color var(--sl-wissel,150ms ease-out),transform var(--sl-wissel,150ms ease-out)}
.knop:hover:not(:disabled){background:var(--sl-blauw-h,#0a1a6b);border-color:var(--sl-blauw-h,#0a1a6b)}
.knop:disabled{background:var(--sl-lijn,#e2e2e0);border-color:var(--sl-lijn,#e2e2e0);
  color:var(--sl-tekst-2,#6b6f7a);cursor:not-allowed}
.knop--licht{background:var(--sl-wit,#fff);color:var(--sl-tekst,#111111);border-color:var(--sl-tekst,#111111)}
.knop--licht:hover:not(:disabled){background:var(--sl-grond,#f2f2f0);border-color:var(--sl-tekst,#111111)}
.knop--licht:disabled{background:var(--sl-wit,#fff);border-color:var(--sl-lijn,#e2e2e0)}
.bevestiging{display:inline-flex;align-items:center;min-height:44px;font-size:14px;font-weight:500}

/* Onderbalk — sticky, nooit fixed (N9): stap n van 5, prijs, Vorige en Volgende (of de bouwknop) */
.onderbalk{position:sticky;bottom:0;z-index:5;border-top:1px solid var(--sl-lijn,#e2e2e0);
  background:var(--sl-wit,#fff)}
.ob-rij{display:flex;align-items:center;justify-content:space-between;gap:16px 24px;flex-wrap:wrap;
  padding:12px 0}
/* Links de build zoals hij nu staat: duim (alleen een echte foto, zoals op de chips), naam,
   en de stap met wat er nog ontbreekt. Rechts het totaal op --t-price (28). */
.ob-bouw{display:flex;align-items:center;gap:12px;flex:1 1 auto;min-width:0}
.ob-duim{flex:none;width:40px;height:40px;object-fit:contain;background:var(--sl-wit,#fff)}
.ob-tekst{display:flex;flex-direction:column;min-width:0}
.ob-naam{font-size:14px;font-weight:500;line-height:1.4;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ob-stap{font-size:13px;line-height:1.45;color:var(--sl-tekst-2,#6b6f7a)}
.ob-prijs{display:flex;flex-direction:column;align-items:flex-end}
.ob-prijs .bedrag{font-size:28px}
.bedrag.nul{color:var(--sl-tekst-2,#6b6f7a)}
.ob-knoppen{display:flex;align-items:center;gap:8px}
@container (max-width:560px){
  .ob-rij{padding:8px 0}
  .ob-bouw{flex-basis:100%}
  .ob-duim,.ob-naam{display:none}
  .ob-prijs{flex:1}
  .ob-prijs .bedrag{font-size:22px}
  .knop{padding:0 16px}
}

/* Foutstaat */
.fout{display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap;
  margin:0 0 16px;padding:12px 16px;border:1px solid var(--sl-rood,#9a1b1b);
  border-radius:var(--sl-r,2px);color:var(--sl-rood,#9a1b1b);
  font-size:14px;line-height:1.5}
.fout svg{flex:none;margin-top:3px}
.fout a{color:var(--sl-rood,#9a1b1b);font-weight:500}
`;

/* Opmaakhelpers op modulehoogte: het gripvak wordt los hertekend en heeft ze ook nodig. */
const waaromRegel = (tekst) => '<p class="waarom">' + esc(tekst) + '</p>';
/* Rick's waarschuwing: tekst in --warn met een lijn erboven, geen vlak, geen icoon. */
const letopBlok = (tekst) => '<p class="letop">' + esc(tekst) + '</p>';
/* De productfoto op een grip- of adapterchip (#93): `foto` is een kleine `image_url` uit de
   payload. Vaste 40×40, dus geen verschuiving als hij binnenkomt. Geen foto = tekstchip. */
const duim = (item) =>
  item && item.foto
    ? '<img class="duim" src="' + esc(item.foto) + '" alt="' + esc(item.foto_alt || '') +
      '" width="40" height="40" loading="lazy">'
    : '';
/* Naam en onderregel; met een foto ervoor komen ze samen in één kolom naast de foto. */
const chipInhoud = (tekst, sub, foto) => {
  const inhoud = esc(tekst) + (sub ? '<span class="sub">' + esc(sub) + '</span>' : '');
  return foto ? foto + '<span>' + inhoud + '</span>' : inhoud;
};
const chipKnop = (sleutel, waarde, tekst, sub, gekozen, klas = '', foto = '') =>
  '<button type="button" class="chip ' + klas + (foto ? ' chip--foto' : '') + '" aria-pressed="' +
  (String(gekozen) === String(waarde)) + '" data-a="set" data-k="' + sleutel + '" data-v="' +
  esc(waarde) + '">' + chipInhoud(tekst, sub, foto) + '</button>';
/* Een niet-kiesbare optie blijft staan, met haar reden — verdwijnen leest als kapot. */
const uitChipKnop = (tekst, reden, klas = '', foto = '') =>
  '<button type="button" class="chip ' + klas + (foto ? ' chip--foto' : '') + '" disabled>' +
  chipInhoud(tekst, reden, foto) + '</button>';
const optieChip = (item, sleutel, tekst, sub, gekozen, klas = 'chip--breed') =>
  item.kiesbaar
    ? chipKnop(sleutel, item.id, tekst, sub, gekozen, klas, duim(item))
    : uitChipKnop(tekst, item.reden, klas, duim(item));
/* Label boven een rij opties. */
const veldBlok = (label, body, id) =>
  '<div class="veld"' + (id ? ' data-in="veld:' + id + '"' : '') + '><span class="vlabel">' + esc(label) + '</span>' + body + '</div>';

/* Eén geadopteerd stylesheet voor alle instanties: de CSS wordt niet per render
   opnieuw geparsed. Oudere browsers vallen terug op een <style> in de shell. */
const BLAD =
  typeof CSSStyleSheet !== 'undefined' && 'replaceSync' in CSSStyleSheet.prototype
    ? (() => {
        const blad = new CSSStyleSheet();
        blad.replaceSync(CSS);
        return blad;
      })()
    : null;

/* Een uitklap groeit en krimpt zoals de zoeklade van het thema (DESIGN.md §7, Rick 26 sep 2026):
   de hoogte in 340ms op de canvascurve, de inhoud fadet 200ms en valt 6px in, 80ms later; dicht
   gaat in 70% van de tijd en de inhoud fadet direct weg. Een klik midden in de beweging draait
   hem om vanaf waar hij is. Het thema heeft dezelfde beweging in assets/beweging.js; dit
   element draagt zijn eigen kopie, want op een gastpagina staat dat script er niet (N7). */
const LADE = { duur: 340, curve: 'cubic-bezier(.22,.61,.36,1)' };
const klapBewegingen = new WeakMap();
function klapUit(details, open) {
  const kop = details.querySelector(':scope > summary');
  const inhoud = [...details.children].filter((el) => el !== kop);
  const wasDicht = !details.open;
  const van = details.getBoundingClientRect().height;
  const dekking = inhoud.map((el) => (wasDicht ? 0 : Number(getComputedStyle(el).opacity)));
  (klapBewegingen.get(details) || []).forEach((a) => a.cancel());

  details.open = true; /* ook bij dichtgaan: de inhoud blijft staan tot de hoogte er is */
  const stijl = getComputedStyle(details);
  const rand = ['borderTopWidth', 'borderBottomWidth', 'paddingTop', 'paddingBottom']
    .reduce((som, eigenschap) => som + parseFloat(stijl[eigenschap]), 0);
  const naar = open ? details.getBoundingClientRect().height : kop.getBoundingClientRect().height + rand;
  const duur = LADE.duur * (open ? 1 : 0.7);

  details.dataset.klap = open ? 'open' : 'dicht';
  details.style.overflow = 'clip';
  const animaties = [details.animate({ height: [van + 'px', naar + 'px'] }, { duration: duur, easing: LADE.curve })];
  inhoud.forEach((el, i) => {
    if (open) {
      const later = wasDicht ? 80 : 0;
      animaties.push(el.animate({ opacity: [dekking[i], 1] }, { duration: 200, delay: later, easing: 'ease-out', fill: 'backwards' }));
      if (wasDicht) animaties.push(el.animate({ translate: ['0 -6px', '0 0'] }, { duration: duur, delay: later, easing: LADE.curve, fill: 'backwards' }));
    } else {
      animaties.push(el.animate({ opacity: [dekking[i], 0] }, { duration: 150, easing: 'ease-out', fill: 'forwards' }));
    }
  });
  klapBewegingen.set(details, animaties);
  /* Afgebroken (omgekeerd, of de boom is herbouwd): dan verwerpt dit en gebeurt er niets. */
  Promise.all(animaties.map((a) => a.finished)).then(() => {
    if (!open) details.open = false;
    animaties.forEach((a) => a.cancel());
    details.style.overflow = '';
    delete details.dataset.klap;
    klapBewegingen.delete(details);
  }, () => {});
}

/* --------------------------------------------------------------------- SVG */
const svg = (binnen, breedte = 1.7) =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="' +
  breedte +
  '" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  binnen +
  '</svg>';

const ICOON = {
  let: svg('<path d="M12 4 2.6 20h18.8L12 4Z"/><path d="M12 10v4.2M12 17v.6"/>'),
  plus: '<span class="plus">' + svg('<path d="M12 5v14M5 12h14"/>') + '</span>',
  min: '<span class="min">' + svg('<path d="M5 12h14"/>') + '</span>',
};

/* ----------------------------------------------------------------- ELEMENT */
/* Het element leest zijn payload uit het `<script type="application/json">`-kind (N11),
   rendert alles in zijn eigen shadow root en raakt niets daarbuiten (N10). Het light-DOM-
   kind — de link naar de shaftpagina (N20) — verdwijnt vanzelf zodra het element upgradet,
   want er is geen <slot>. */

/* Welke keuzes een getal dragen; de rest is tekst of, bij spining, een vlag. */
const GETALSLEUTELS = new Set(['shaft', 'adapter', 'grip', 'lengte', 'tipping', 'griplaagdikte']);
const GRIPCAP = 24;
/* Gripmaat (#106): de metaobject-handles van `Golf grip size`, in chipvolgorde. Anders en
   Oversize +1/8 krijgen geen chip: die grips staan alleen onder Alle maten. */
const GRIPMATEN = ['undersize', 'standaard', 'midsize', 'jumbo'];
/* Mijn specs: dezelfde sleutel als `assets/specs.js` (docs/paginastructuur.md §3). */
const SPECSLEUTEL = 'shaftlab.specs';

/* De klasse leeft binnen de guard: `HTMLElement` bestaat niet in Node, en zo blijft de
   module daar importeerbaar (spec #37 §E). Twee keer laden is geen harde fout (N4). */
if (typeof customElements !== 'undefined' && !customElements.get('shaftlab-configurator')) {
  customElements.define(
    'shaftlab-configurator',
    class extends HTMLElement {
      connectedCallback() {
        if (this.root) return;
        const blok = this.querySelector('script[type="application/json"]');
        try {
          this.payload = JSON.parse(blok.textContent);
        } catch {
          return; /* geen geldige catalogus: de link (N20) blijft de weg vooruit */
        }
        /* De taal van de pagina, bij een gastpagina die van de gast (#86). */
        const taalDrager = this.closest('[lang]');
        kiesTaal(taalDrager ? taalDrager.getAttribute('lang') : document.documentElement.lang, this.payload.teksten);
        this.keuzes = uitQuery(this.payload, location.search);
        const variant = this.payload.shaft.varianten.find((v) => v.id === this.keuzes.shaft);
        this.ui = {
          stap: stapUitQuery(location.search),
          flex: variant ? variant.flex : null,
          merkA: merkUitQuery(this.payload, location.search),
          gripMerk: '',
          gripMaat: '',
          gripZoek: '',
          open: new Set(),
          openAfw: new Set(),
          fijn: false,
          fout: null,
          bezig: false,
          weg: verdwenen(this.payload, location.search).length > 0,
          bewaard: false,
        };
        this.afbreker = new AbortController();
        const signal = this.afbreker.signal;
        this.root = this.attachShadow({ mode: 'open' });
        /* De shell wordt één keer gezet: het melderveld is de enige live-regio van het
           element, en overleeft daarmee elke herbouw van `#hoofd`. */
        if (BLAD) this.root.adoptedStyleSheets = [BLAD];
        this.root.innerHTML =
          (BLAD ? '' : '<style>' + CSS + '</style>') +
          '<p class="vh" id="melder" aria-live="polite"></p><div id="hoofd"></div>';
        this.hoofd = this.root.getElementById('hoofd');
        this.melder = this.root.getElementById('melder');
        this.root.addEventListener('click', (e) => this.klapBeweging(e), { signal });
        this.root.addEventListener('click', (e) => this.klik(e), { signal });
        this.root.addEventListener('input', (e) => this.invoer(e), { signal });
        this.root.addEventListener('toggle', (e) => this.klap(e), { signal, capture: true });
        /* Een muis wijst een chip aan vóór hij klikt, een vinger drukt vóór de klik: in die
           tijd ligt de vlakfoto al klaar. */
        for (const soort of ['pointerover', 'pointerdown', 'focusin']) {
          this.root.addEventListener(soort, (e) => this.voorlaad(e), { signal, passive: true });
        }
        this.render();
      }

      /** Alles wat we opzetten hangt aan één signal, dus opruimen is één regel (N10). */
      disconnectedCallback() {
        if (this.afbreker) this.afbreker.abort();
      }

      /* ------------------------------------------------------------ GEDRAG */

      /** Eén keuze zetten en de query bijwerken — replaceState, nooit pushState (N9). */
      kies(sleutel, waarde) {
        if (!this.payload) return;
        this.keuzes = muteer(this.payload, this.keuzes, sleutel, waarde);
        this.ui.bewaard = false;
        this.schrijfUrl();
      }

      /* De stap reist mee in de query (docs/paginastructuur.md §3), ook met replaceState:
         de terugknop blijft paginaniveau en sleept niet door stappen. */
      schrijfUrl() {
        const query = naarQuery(this.keuzes, this.ui.stap > 1 ? this.ui.stap : undefined);
        history.replaceState(null, '', query ? location.pathname + '?' + query : location.pathname);
      }

      /** Naar een stap; daarna de kop in beeld en in focus, zodat de wissel hoorbaar is. */
      ga(stap) {
        this.ui.stap = Math.min(STAPPEN, Math.max(1, stap));
        this.schrijfUrl();
        this.render();
        const kop = this.root.querySelector('.staptitel');
        if (!kop) return;
        const rustig = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: rustig ? 'auto' : 'smooth' });
        kop.focus({ preventScroll: true });
      }

      /** De vlakfoto van de aangewezen grip of adapter alvast ophalen, met dezelfde srcset en
          sizes als het vlak, zodat de browser hetzelfde bestand kiest. Eén keer per foto. */
      voorlaad(gebeurtenis) {
        const chip = gebeurtenis.target.closest && gebeurtenis.target.closest('.chip--foto');
        if (!chip || !this.payload) return;
        const { a, k, v } = chip.dataset;
        const item =
          k === 'grip' ? this.payload.grips.find((g) => String(g.id) === v)
          : k === 'adapter' ? this.payload.adapters.find((x) => String(x.id) === v)
          : a === 'merk' ? this.payload.adapters.find((x) => x.merk === v)
          : null;
        if (!item || !item.foto) return;
        this.geladen = this.geladen || new Set();
        if (this.geladen.has(item.foto)) return;
        this.geladen.add(item.foto);
        const beeld = new Image();
        beeld.sizes = VLAKSIZES;
        beeld.srcset = vlakSrcset(item.foto);
        beeld.src = opMaat(item.foto, 800);
      }

      evalueer() {
        return this.payload ? evalueer(this.payload, this.keuzes) : null;
      }

      klik(gebeurtenis) {
        const knop = gebeurtenis.target.closest('[data-a]');
        if (!knop || !this.payload) return;
        const { a, k, v } = knop.dataset;
        if (a === 'set') {
          this.kies(k, k === 'spining' ? v === 'true' : GETALSLEUTELS.has(k) ? Number(v) : v);
          this.ui.fout = null;
          this.ui.weg = false;
        } else if (a === 'flex') {
          /* Flex kiezen wist een variant met een andere flex; één gewicht in deze flex
             kiest zichzelf. */
          this.ui.flex = v;
          const inFlex = this.evalueer().shaftVarianten.filter((x) => x.flex === v && x.kiesbaar);
          if (inFlex.length === 1) this.kies('shaft', inFlex[0].id);
          else if (this.keuzes.shaft != null && !inFlex.some((x) => x.id === this.keuzes.shaft)) this.kies('shaft', null);
        } else if (a === 'merk') {
          this.ui.merkA = v || null;
          /* Eén adapter onder dit merk: dan is er geen generatiestap om te tonen. */
          const passend = this.evalueer().adapters.filter((x) => x.merk === v);
          if (passend.length === 1 && passend[0].kiesbaar) this.kies('adapter', passend[0].id);
          else if (this.keuzes.adapter != null) this.kies('adapter', null);
        } else if (a === 'gripmerk') {
          this.ui.gripMerk = v || '';
        } else if (a === 'gripmaat') {
          this.ui.gripMaat = v || '';
        } else if (a === 'fijn') {
          this.ui.fijn = !this.ui.fijn;
        } else if (a === 'vorige') {
          return this.ga(this.ui.stap - 1);
        } else if (a === 'volgende') {
          if (!stapCompleet(this.payload, this.keuzes, this.ui.stap)) return;
          return this.ga(this.ui.stap + 1);
        } else if (a === 'bewaar') {
          this.bewaar();
        } else if (a === 'bouw') {
          this.bouw();
          return;
        } else return;
        this.render();
      }

      /* Typen in het zoekveld hertekent alleen het gripvak: de rest van de boom — en
         daarmee de focus en de cursorpositie — blijft staan waar hij stond. */
      invoer(gebeurtenis) {
        if (gebeurtenis.target.dataset.a !== 'zoek') return;
        this.ui.gripZoek = gebeurtenis.target.value;
        const vak = this.root.getElementById('gripvak');
        if (!vak) return this.render();
        vak.innerHTML = this.gripVak();
        this.meld(this.gripMelding);
      }

      /** Een klik op een <summary>: de uitklap beweegt (klapUit) in plaats van te springen. De
          staat gaat meteen in `ui`, zodat een herbouw midden in de beweging hem niet terugzet;
          het toggle-event hieronder zegt daarna hetzelfde. Onder reduced motion: native. */
      klapBeweging(gebeurtenis) {
        const kop = gebeurtenis.target.closest('summary');
        const details = kop && kop.parentElement;
        if (!details || details.localName !== 'details') return;
        if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        gebeurtenis.preventDefault();
        const open = !details.open || details.dataset.klap === 'dicht';
        const { uitleg, afw } = details.dataset;
        const lijst = uitleg ? this.ui.open : afw ? this.ui.openAfw : null;
        if (lijst) lijst[open ? 'add' : 'delete'](uitleg || afw);
        klapUit(details, open);
      }

      /* Een uitklap overleeft de volgende render alleen als we onthouden dat hij open stond:
         de uitleg en de afwerkingsrij. */
      klap(gebeurtenis) {
        const details = gebeurtenis.target;
        if (!details.dataset) return;
        const { uitleg, afw } = details.dataset;
        if (uitleg) {
          if (details.open) this.ui.open.add(uitleg);
          else this.ui.open.delete(uitleg);
        } else if (afw) {
          if (details.open) this.ui.openAfw.add(afw);
          else this.ui.openAfw.delete(afw);
        }
      }

      /** Mijn specs: de spec bovenaan `localStorage['shaftlab.specs']` (paginastructuur §3).
          ponytail: alleen deze browser; het upgradepad is een klantmetafield via app-proxy. */
      bewaar() {
        try {
          localStorage.setItem(SPECSLEUTEL, JSON.stringify(bewaarSpec(localStorage.getItem(SPECSLEUTEL), this.payload, this.keuzes)));
          this.ui.bewaard = true;
        } catch {
          this.ui.fout = STRINGS.fout_storing; /* privémodus zonder opslag: de storingszin, geen stille faal */
        }
      }

      /** De koopaanroep: simple request, geen custom headers, body als tekst (N23). */
      async bouw() {
        const uit = this.evalueer();
        if (!uit || !uit.compleet || this.ui.bezig) return;
        this.ui.bezig = true;
        this.ui.fout = null;
        this.render();

        let status = 0;
        let antwoord = null;
        try {
          const respons = await fetch(this.payload.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify(this.keuzes),
          });
          status = respons.status;
          antwoord = await respons.json().catch(() => null);
        } catch {
          status = 0; /* netwerkfout leest als storing (geval 7) */
        }

        const gevolg = koopgevolg(this.payload, status, antwoord);
        if (gevolg.melding) {
          this.ui.bezig = false;
          this.ui.fout = gevolg.melding;
          this.render();
          return;
        }

        /* Thuis: de regel erbij via add.js en dan naar de kar (#90); elders de permalink. */
        const weg = koopweg(gevolg, location.href);
        if (weg.permalink) {
          location.assign(weg.permalink);
          return;
        }
        let toegevoegd = false;
        try {
          const respons = await fetch(weg.toevoegen, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(weg.body),
          });
          toegevoegd = respons.ok;
        } catch {
          toegevoegd = false;
        }
        if (toegevoegd) {
          location.assign(weg.kar);
          return;
        }
        this.ui.bezig = false;
        this.ui.fout = STRINGS.fout_storing; /* de bundel bestaat maar ligt niet in de kar: storing, geen stille faal */
        this.render();
      }

      /* ------------------------------------------------------------ OPMAAK */

      render() {
        if (!this.payload) return;
        const actief = this.root.activeElement;
        const sleutel = actief && actief.dataset ? JSON.stringify(actief.dataset) : null;
        const caret = actief && actief.dataset && actief.dataset.a === 'zoek' ? actief.selectionStart : null;

        /* Identiteit van wat er nu staat; na de render is alles zonder oude identiteit "nieuw"
           en beweegt één keer (zie CSS "Beweging"). Een gekozen chip telt als nieuw zodra hij
           gekozen is. De eerste render staat stil. */
        const id = (n) => n.dataset.in || 'chip:' + n.dataset.a + ':' + n.dataset.k + ':' + n.dataset.v;
        const NIEUW = '[data-in],.chip[aria-pressed="true"]';
        const was = new Set([...this.root.querySelectorAll(NIEUW)].map(id));

        /* WebKit (iOS Safari) springt naar de onderkant van de pagina als de boom onder een
           tik wordt vervangen — de scrollpositie moet dus over de herbouw heen worden
           gedragen. Het lezen vóór de herbouw is onderdeel van de fix: het dwingt de
           openstaande lay-out af. Chrome heeft er geen last van. */
        const scrollStand = window.scrollY;
        this.hoofd.innerHTML = this.html();
        if (window.scrollY !== scrollStand) window.scrollTo({ top: scrollStand, behavior: 'instant' });

        if (was.size) for (const n of this.root.querySelectorAll(NIEUW)) if (!was.has(id(n))) n.classList.add('in');

        if (sleutel) {
          const terug = [...this.root.querySelectorAll('[data-a]')].find(
            (n) => JSON.stringify(n.dataset) === sleutel
          );
          if (terug) {
            terug.focus({ preventScroll: true });
            if (caret !== null && terug.setSelectionRange) terug.setSelectionRange(caret, caret);
          }
        }
        this.meld();
      }

      /** De enige aankondiging van het element: stap, bedrag en wat er nog ontbreekt.
          Eén regio in plaats van vijf, zodat een screenreader een wijziging hoort
          in plaats van de hele boom opnieuw. */
      meld(tekst) {
        if (!this.melder) return;
        if (!this.gestart) {
          this.gestart = true;
          return; /* de eerste opbouw is geen wijziging */
        }
        const uit = this.evalueer();
        const regel =
          tekst ||
          STRINGS.stap_van(this.ui.stap, STAPPEN) + ' — ' +
            eur(uit.prijs, this.payload.valuta) +
            ' — ' +
            (uit.compleet ? STRINGS.compleet : STRINGS.ontbreekt(this.ontbreekt(uit)));
        if (regel === this.vorigeMelding) return;
        this.vorigeMelding = regel;
        this.melder.textContent = regel;
      }

      /** Wat er nog gekozen moet worden; de melder leest dit. */
      ontbreekt(uit) {
        const k = this.keuzes;
        return [
          !k.club && STRINGS.ontbreekt_club,
          !k.shaft && STRINGS.ontbreekt_shaft,
          k.club && !k.adapter && STRINGS.ontbreekt_adapter,
          !k.grip && STRINGS.ontbreekt_grip,
        ].filter(Boolean);
      }

      /** Alleen de griptegels: het enige stuk dat per toetsaanslag verandert. */
      gripVak() {
        const uit = this.evalueer();
        const geld = (centen) => eur(centen, this.payload.valuta);
        const zoek = this.ui.gripZoek.trim().toLowerCase();
        let lijst = uit.grips.filter(
          (g) =>
            (!this.ui.gripMerk || g.merk === this.ui.gripMerk) &&
            (!this.ui.gripMaat || g.gripmaat === this.ui.gripMaat) &&
            (!zoek ||
              (g.titel + ' ' + g.merk + ' ' + g.maat + ' ' + (g.kleur || ''))
                .toLowerCase()
                .includes(zoek))
        );
        const totaal = lijst.length;
        lijst = lijst.slice(0, GRIPCAP);
        const gekozen = uit.grips.find((g) => g.id === this.keuzes.grip);
        if (gekozen && !lijst.some((g) => g.id === gekozen.id)) lijst.unshift(gekozen);

        this.gripMelding =
          (uit.gripsReden ? uit.gripsReden + ' ' : '') +
          STRINGS.waarom_grips(Math.min(totaal, GRIPCAP), this.payload.grips.length) +
          (totaal > GRIPCAP ? ' ' + STRINGS.waarom_grips_cap(GRIPCAP) : '');

        return (
          '<div class="chips">' +
          lijst
            .map((g) =>
              optieChip(
                g,
                'grip',
                g.titel,
                [(g.kleur || '').trim(), g.maat, geld(g.prijs)].filter(Boolean).join(' · '),
                this.keuzes.grip
              )
            )
            .join('') +
          '</div>' +
          waaromRegel(this.gripMelding)
        );
      }

      /* De vijf stappen, één tegelijk (canvas 3c). Elke stap levert titel, intro en body;
         de onderbalk telt mee en draagt Vorige, Volgende of — op de laatste stap — de
         bouwknop. */
      html() {
        const payload = this.payload;
        const keuzes = this.keuzes;
        const uit = this.evalueer();
        const geld = (centen) => eur(centen, payload.valuta);
        const shaft = payload.shaft;
        const hybride = isHybrideshaft(payload);
        const club = CLUBS.find((c) => c.id === keuzes.club) || null;
        const stap = this.ui.stap;

        const uitklap = (naam) => {
          const tekst = (payload.uitleg || {})[naam];
          if (!tekst) return ''; /* ontbrekende tekst = geen uitklap (N12) */
          return (
            '<details class="uitklap" data-uitleg="' + naam + '"' + (this.ui.open.has(naam) ? ' open' : '') +
            '><summary>' + ICOON.plus + ICOON.min + STRINGS.uitklap + '</summary><p>' + esc(tekst) + '</p></details>'
          );
        };
        const chip = chipKnop;
        const uitChip = uitChipKnop;
        const optie = (item, sleutel, tekst, sub, klas) => optieChip(item, sleutel, tekst, sub, keuzes[sleutel], klas);
        const afw = (sleutel, naam, waarde, body) =>
          '<details class="afw" data-afw="' + sleutel + '"' +
          (this.ui.openAfw.has(sleutel) ? ' open' : '') +
          '><summary><span>' + esc(naam) + ' <span class="aw">— ' + esc(waarde) + '</span></span>' +
          ICOON.plus + '</summary><div class="binnen">' + body + '</div></details>';

        const titel = STRINGS.stap_titel[stap];
        let intro = '';
        let body = '';

        if (stap === 1) {
          /* — 1 Club: hand en Club, met de standaardlengte op elke tegel — */
          /* De vertrouwenszin staat ook hier, niet pas in het overzicht: wie twijfelt of hij
             zonder fitting durft, haakt bij stap 1 af (walkthrough 13 sep, punt 3). */
          intro = STRINGS.waarom_club(shaft.tip_diameter, hybride) + ' ' + STRINGS.vertrouwen(payload.levertijd || STRINGS.levertijd);
          body =
            veldBlok(
              STRINGS.veld_hand,
              '<div class="chips">' +
                chip('hand', 'rechts', STRINGS.hand_rechts, '', keuzes.hand) +
                chip('hand', 'links', STRINGS.hand_links, '', keuzes.hand) +
                '</div>'
            ) +
            veldBlok(
              STRINGS.veld_club,
              '<div class="chips">' +
                uit.clubs
                  .map((c) => (c.kiesbaar ? chip('club', c.id, clubNaam(c), maat(c.standaardlengte), keuzes.club) : uitChip(clubNaam(c), c.reden)))
                  .join('') +
                '</div>' + waaromRegel(STRINGS.hulp_club)
            ) +
            uitklap('tipdiameter');
        } else if (stap === 2) {
          /* — 2 Flex en gewicht: twee groepen die samen één variant aanwijzen — */
          intro = STRINGS.hulp_shaft;
          const flexen = [...new Set(uit.shaftVarianten.map((v) => v.flex))];
          const gekozenVariant = uit.shaftVarianten.find((v) => v.id === keuzes.shaft);
          const flex = gekozenVariant ? gekozenVariant.flex : flexen.includes(this.ui.flex) ? this.ui.flex : null;
          const gewichten = [...new Set(uit.shaftVarianten.map((v) => v.gewicht))];
          body =
            veldBlok(
              STRINGS.veld_flex,
              '<div class="chips">' +
                flexen
                  .map(
                    (f) =>
                      '<button type="button" class="chip" aria-pressed="' + (flex === f) +
                      '" data-a="flex" data-v="' + esc(f) + '">' + esc(f) + '</button>'
                  )
                  .join('') +
                '</div>'
            ) +
            veldBlok(
              STRINGS.veld_gewicht,
              flex
                ? '<div class="chips">' +
                  gewichten
                    .map((g) => {
                      const v = uit.shaftVarianten.find((x) => x.flex === flex && x.gewicht === g);
                      return v ? optie(v, 'shaft', g, geld(v.prijs), '') : uitChip(g, STRINGS.reden_gewicht);
                    })
                    .join('') +
                  '</div>'
                : '<p class="stil">' + STRINGS.kies_flex_eerst + '</p>',
              flex ? 'gewicht:' + flex : ''
            ) +
            uitklap('flex');
        } else if (stap === 3) {
          /* — 3 Grip: merk-chips, maat-chips, zoekveld, tegels. De tegels leven in `#gripvak`, zodat
               een toetsaanslag alleen dat vak hertekent en de cursor in het zoekveld met rust laat.
               Een maat zonder grips in deze lijst krijgt geen chip, net als een merk. — */
          intro = STRINGS.intro_grip;
          const gripMerken = [...new Set(uit.grips.map((g) => g.merk))];
          const gripMaten = GRIPMATEN.filter((m) => uit.grips.some((g) => g.gripmaat === m));
          /* Een maat zonder chip (andere hand, terug vanuit een latere stap) filtert niet stil door. */
          if (!gripMaten.includes(this.ui.gripMaat)) this.ui.gripMaat = '';
          const maatChip = (m, naam) =>
            '<button type="button" class="chip" aria-pressed="' + (this.ui.gripMaat === m) +
            '" data-a="gripmaat" data-v="' + m + '">' + esc(naam) + '</button>';
          body =
            '<input type="search" class="zoekveld" data-a="zoek" aria-label="' + STRINGS.grip_zoek_label +
            '" placeholder="' + STRINGS.grip_zoek + '" value="' + esc(this.ui.gripZoek) + '">' +
            '<div class="chips" style="margin-bottom:12px">' +
            '<button type="button" class="chip" aria-pressed="' + !this.ui.gripMerk +
            '" data-a="gripmerk" data-v="">' + STRINGS.alle_merken + '</button>' +
            gripMerken
              .map(
                (m) =>
                  '<button type="button" class="chip" aria-pressed="' + (this.ui.gripMerk === m) +
                  '" data-a="gripmerk" data-v="' + esc(m) + '">' + esc(m) + '</button>'
              )
              .join('') +
            '</div>' +
            (gripMaten.length
              ? '<div class="chips" role="group" aria-label="' + esc(STRINGS.maat_filter) +
                '" style="margin-bottom:12px">' + maatChip('', STRINGS.alle_maten) +
                gripMaten.map((m) => maatChip(m, STRINGS['maat_' + m])).join('') + '</div>'
              : '') +
            '<div id="gripvak">' + this.gripVak() + '</div>';
        } else if (stap === 4) {
          /* — 4 Adapter: merk, dan generatie — gefilterd op tip én club_soort — */
          intro = STRINGS.intro_adapter(shaft.tip_diameter);
          if (!club) {
            body = '<p class="stil">' + STRINGS.kies_club_eerst + '</p>' + uitklap('adapter');
          } else {
            const merken = [...new Set(uit.adapters.map((a) => a.merk))];
            const gekozenAdapter = uit.adapters.find((a) => a.id === keuzes.adapter);
            const merkA = gekozenAdapter ? gekozenAdapter.merk : merken.includes(this.ui.merkA) ? this.ui.merkA : null;
            const generaties = merkA ? uit.adapters.filter((a) => a.merk === merkA) : [];
            body =
              veldBlok(
                STRINGS.veld_adaptermerk,
                '<div class="chips">' +
                  merken
                    .map((m) => {
                      /* Eén adapter onder dit merk: de merkchip ís dan de keuze en draagt zijn foto —
                         behalve als die adapter niet kiesbaar is; de merkchip kan niet uit. */
                      const vanMerk = uit.adapters.filter((a) => a.merk === m);
                      const foto = vanMerk.length === 1 && vanMerk[0].kiesbaar ? duim(vanMerk[0]) : '';
                      return (
                        '<button type="button" class="chip' + (foto ? ' chip--foto' : '') + '" aria-pressed="' + (merkA === m) +
                        '" data-a="merk" data-v="' + esc(m) + '">' + chipInhoud(m, STRINGS.generaties(vanMerk.length), foto) +
                        '</button>'
                      );
                    })
                    .join('') +
                  '</div>' +
                  waaromRegel(uit.adaptersReden || STRINGS.waarom_adapters(uit.adapters.length, shaft.tip_diameter)) +
                  letopBlok(STRINGS.letop_kop)
              ) +
              /* De generatiestap verschijnt alleen als dit merk er meer dan één heeft. */
              (merkA && generaties.length > 1
                ? veldBlok(
                    STRINGS.veld_adaptergeneratie,
                    '<div class="chips">' +
                      generaties.map((a) => optie(a, 'adapter', a.generatie || a.titel, geld(a.prijs))).join('') +
                      '</div>' + waaromRegel(STRINGS.waarom_generaties(generaties.length, merkA)),
                    'generatie:' + merkA
                  )
                : '') +
              uitklap('adapter');
          }
        } else {
          /* — 5 Afwerking: lengte met het voorbehoud eronder, vier dichtgeklapte rijen, het overzicht — */
          intro = STRINGS.intro_afwerking;
          const u = uit.uitlezing;
          const lengteBody = !club
            ? '<p class="stil">' + STRINGS.volgt_uit_club + '</p>'
            : '<div class="uitlezing">' +
              '<span>' + maat(u.standaard) + '</span><span class="zacht">' + STRINGS.standaard + '</span>' +
              '<span class="zacht">' + (u.afwijking < 0 ? '−' : '+') + '</span>' +
              '<span>' + maat(Math.abs(u.afwijking)) + '</span><span class="zacht">=</span>' +
              '<span class="uit">' + maat(u.uitkomst) + '</span></div>' +
              '<div class="chips">' +
              (this.ui.fijn || !LENGTES_GROF.includes(keuzes.lengte)
                ? uit.lengtes
                : uit.lengtes.filter((l) => LENGTES_GROF.includes(l.waarde))
              )
                .map((l) =>
                  l.kiesbaar
                    ? chip('lengte', l.waarde, l.waarde === 0 ? STRINGS.lengte_standaard : afwijking(l.waarde, STRINGS.decimaal),
                        l.uitkomst === null ? '' : maat(l.uitkomst), keuzes.lengte)
                    : uitChip(l.waarde === 0 ? STRINGS.lengte_standaard : afwijking(l.waarde, STRINGS.decimaal), l.reden)
                )
                .join('') +
              '</div>' +
              (LENGTES_GROF.includes(keuzes.lengte)
                ? '<button type="button" class="meer" data-a="fijn">' + (this.ui.fijn ? STRINGS.lengte_grof : STRINGS.lengte_fijn) + '</button>'
                : '') +
              letopBlok(STRINGS.letop_lengte);

          const tippingBody =
            '<div class="chips">' +
            uit.tipping
              .map((t) => {
                const naam = t.waarde ? maat(t.waarde) : STRINGS.tipping_geen;
                const sub = t.waarde ? (t.waarde >= 1 ? STRINGS.tipping_stuk : STRINGS.tipping_iets) : STRINGS.standaard;
                return t.kiesbaar ? chip('tipping', t.waarde, naam, sub, keuzes.tipping) : uitChip(naam, t.reden);
              })
              .join('') +
            '</div>' + uitklap('tipping');
          const laagBody =
            '<div class="chips">' +
            GRIPLAAGDIKTES.map((n) =>
              chip('griplaagdikte', n, STRINGS.griplaagdikte[n], n === 0 ? STRINGS.standaard : STRINGS.laag_sub(n), keuzes.griplaagdikte)
            ).join('') +
            '</div>' + uitklap('griplaagdikte');
          const logoBody =
            '<div class="chips">' +
            chip('griplogo', 'boven', STRINGS.logo_boven, STRINGS.standaard, keuzes.griplogo) +
            chip('griplogo', 'onder', STRINGS.logo_onder, '', keuzes.griplogo) +
            '</div>';
          const spinBody =
            '<div class="chips">' +
            chip('spining', 'false', STRINGS.spining_niet, STRINGS.standaard, String(keuzes.spining)) +
            chip('spining', 'true', STRINGS.spining_wel, STRINGS.spining_sub, String(keuzes.spining)) +
            '</div>' + uitklap('spining');

          /* Het overzicht: onderdelen met prijs, bewerkingen zonder ("—"), totaal als som. */
          const gekozenShaft = shaft.varianten.find((v) => v.id === keuzes.shaft);
          const gekozenAdapter = payload.adapters.find((a) => a.id === keuzes.adapter);
          const gekozenGrip = payload.grips.find((g) => g.id === keuzes.grip);
          const regel = (label, waarde, prijs) =>
            '<div class="regel"><span class="l">' + esc(label) + '</span><span class="w' + (waarde ? '' : ' leeg') + '">' +
            esc(waarde || STRINGS.sv_nog_kiezen) + '<span class="p">' + esc(prijs === undefined ? STRINGS.sv_geen : prijs) + '</span></span></div>';
          const overzicht =
            '<div class="overzicht"><h3>' + STRINGS.overzicht + '</h3>' +
            '<p class="stil" style="font-size:13px;margin-bottom:8px">' +
            esc([clubNaam(club), keuzes.hand === 'links' ? STRINGS.hand_links : STRINGS.hand_rechts].filter(Boolean).join(' · ')) + '</p>' +
            regel(STRINGS.sv_shaft, gekozenShaft ? shaft.titel + ' · ' + gekozenShaft.flex + ' · ' + gekozenShaft.gewicht : '', gekozenShaft ? geld(gekozenShaft.prijs) : '') +
            regel(STRINGS.sv_grip, gekozenGrip ? gekozenGrip.titel + ' · ' + gekozenGrip.maat : '', gekozenGrip ? geld(gekozenGrip.prijs) : '') +
            regel(STRINGS.sv_adapter, gekozenAdapter ? [gekozenAdapter.merk, gekozenAdapter.generatie].filter(Boolean).join(' ') : '', gekozenAdapter ? geld(gekozenAdapter.prijs) : '') +
            regel(STRINGS.sv_lengte, u.uitkomst === null ? '' : maat(u.uitkomst) + ' (' + afwijking(u.afwijking, STRINGS.decimaal) + ')') +
            regel(STRINGS.sv_tipping, keuzes.tipping ? maat(keuzes.tipping) : STRINGS.tipping_geen) +
            regel(STRINGS.sv_griplaagdikte, STRINGS.griplaagdikte[keuzes.griplaagdikte]) +
            regel(STRINGS.sv_griplogo, keuzes.griplogo === 'onder' ? STRINGS.logo_onder : STRINGS.logo_boven) +
            regel(STRINGS.sv_spining, keuzes.spining ? STRINGS.ja : STRINGS.nee) +
            regel(STRINGS.sv_levertijd, payload.levertijd || STRINGS.levertijd) +
            '<div class="totaal"><span class="l">' + STRINGS.sv_totaal + '</span><span class="w"><span class="bedrag">' +
            geld(uit.prijs) + '</span><span class="noot">' + STRINGS.som_onderdelen + '</span></span></div>' +
            '<div class="acties">' +
            (this.ui.bewaard
              ? '<span class="bevestiging" data-in="bewaard">' + STRINGS.bewaard + '</span>'
              : '<button type="button" class="knop knop--licht" data-a="bewaar"' + (uit.compleet ? '' : ' disabled') + '>' + STRINGS.bewaar + '</button>') +
            '</div>' +
            '<p class="stil" style="margin-top:12px;font-size:13px">' + esc(STRINGS.vertrouwen(payload.levertijd || STRINGS.levertijd)) + '</p>' +
            '<p class="stil" style="margin-top:4px;font-size:13px">' + STRINGS.maatwerknoot + '</p></div>';

          body =
            veldBlok(STRINGS.veld_lengte, lengteBody) +
            '<div>' +
            afw('tipping', STRINGS.veld_tipping, keuzes.tipping ? maat(keuzes.tipping) : STRINGS.tipping_geen, tippingBody) +
            afw('griplaagdikte', STRINGS.veld_griplaagdikte, STRINGS.griplaagdikte[keuzes.griplaagdikte], laagBody) +
            afw('griplogo', STRINGS.veld_griplogo, keuzes.griplogo === 'onder' ? STRINGS.logo_onder : STRINGS.logo_boven, logoBody) +
            afw('spining', STRINGS.veld_spining, keuzes.spining ? STRINGS.spining_wel : STRINGS.spining_niet, spinBody) +
            '</div>' +
            overzicht;
        }

        /* — Foutstaat: draagt altijd de link naar de shaftpagina (N25) — bij een gastpagina
             is dat de enige uitweg, en de URL zit al in de payload (N17). — */
        const melding = this.ui.fout || (this.ui.weg ? STRINGS.fout_weg : null);
        const foutblok = (tekst) =>
          tekst
            ? '<div class="fout" role="alert" data-in="fout:' + esc(tekst) + '">' + ICOON.let + '<span>' + esc(tekst) + '</span>' +
              '<a href="' + esc(payload.shaftUrl) + '">' + STRINGS.fout_shaftlink + '</a></div>'
            : '';

        const kv = shaft.varianten.find((v) => v.id === keuzes.shaft);
        const kg = payload.grips.find((g) => g.id === keuzes.grip);
        const ka = payload.adapters.find((a) => a.id === keuzes.adapter);

        /* — Stapvlak (canvas 3c): het onderdeel van deze stap in beeld, naam en spec eronder
             zoals op een productvlak. Grip en adapter zodra ze gekozen zijn en een foto
             hebben, anders de shaft. De AI-plaatshouder staat op --panel en draagt de tag
             (DESIGN.md "Wat niet meebuigt"). Het vlak herhaalt wat voortgang en chips al
             zeggen, dus aria-hidden; het wisselt met een fade zodra zijn foto wisselt. — */
        const vlakVan = (beeld, voorbeeld, naam, spec) => {
          if (!beeld) return ''; /* een payload zonder shaftfoto: geen vlak, de stap neemt de volle breedte */
          spec = spec.filter(Boolean).join(' · ');
          return (
            '<figure class="vlak' + (voorbeeld ? ' vlak--voorbeeld' : '') + '" aria-hidden="true" data-in="vlak:' + esc(beeld) + '">' +
            /* Onder de scherpe foto ligt de 96px-foto van de chip, die al in de cache zit: het
               vlak wisselt meteen, en de scherpe foto tekent eroverheen zodra hij binnen is.
               Alleen de eerste opbouw laadt lui — het vlak staat dan nog onder de vouw. */
            '<div class="vlak-beeld">' +
            (vlakSrcset(beeld) ? '<img class="vlak-voor" src="' + esc(opMaat(beeld, 96)) + '" alt="">' : '') +
            '<img src="' + esc(opMaat(beeld, 800)) + '"' +
            (vlakSrcset(beeld) ? ' srcset="' + esc(vlakSrcset(beeld)) + '" sizes="' + VLAKSIZES + '"' : '') +
            ' alt=""' + (this.gestart ? '' : ' loading="lazy"') + ' decoding="async">' +
            (voorbeeld ? '<span class="tag">' + esc(STRINGS.voorbeeldfoto) + '</span>' : '') + '</div>' +
            '<figcaption><span class="vlak-naam">' + esc(naam) + '</span>' +
            (spec ? '<span class="vlak-spec">' + esc(spec) + '</span>' : '') + '</figcaption></figure>'
          );
        };
        const vlak =
          stap === 3 && kg && kg.foto
            ? vlakVan(kg.foto, false, kg.titel, [kg.maat, geld(kg.prijs)])
            : stap === 4 && ka && ka.foto
              ? vlakVan(ka.foto, false, ka.titel, [geld(ka.prijs)])
              : vlakVan(shaft.foto, !!shaft.voorbeeld, shaft.titel, [clubNaam(club), kv && kv.flex, kv && kv.gewicht, kv && geld(kv.prijs)]);

        /* — Onderbalk: de build (duim, naam, stap n van 5 en wat er nog ontbreekt), prijs op
             --t-price, Vorige en Volgende; de bouwknop vervangt Volgende op de laatste stap — */
        const laatste = stap === STAPPEN;
        const compleet = stapCompleet(payload, keuzes, stap);
        const nogKiezen = this.ontbreekt(uit);
        const status =
          STRINGS.stap_van(stap, STAPPEN) +
          (uit.compleet ? ' · ' + STRINGS.compleet : nogKiezen.length ? ' · ' + STRINGS.ontbreekt(nogKiezen) : '');
        const onderbalk =
          '<div class="onderbalk"><div class="ob-rij">' +
          '<div class="ob-bouw">' +
          (shaft.foto && !shaft.voorbeeld
            ? '<img class="ob-duim" src="' + esc(opMaat(shaft.foto, 96)) + '" alt="" width="40" height="40" loading="lazy">'
            : '') +
          '<span class="ob-tekst"><span class="ob-naam">' + esc(shaft.titel) + '</span>' +
          '<span class="ob-stap">' + esc(status) + '</span></span></div>' +
          '<div class="ob-prijs"><span class="bedrag' + (uit.prijs ? '' : ' nul') + '" data-in="prijs:' + uit.prijs + '">' + geld(uit.prijs) + '</span><span class="noot">' + STRINGS.som_onderdelen + '</span></div>' +
          '<div class="ob-knoppen">' +
          (stap > 1 ? '<button type="button" class="knop knop--licht" data-a="vorige">' + STRINGS.vorige + '</button>' : '') +
          (laatste
            ? '<button type="button" class="knop" data-a="bouw"' + (uit.compleet && !this.ui.bezig ? ' data-in="bouw"' : ' disabled') + '>' +
              (this.ui.bezig ? STRINGS.bouwknop_bezig : STRINGS.bouwknop) + '</button>'
            : '<button type="button" class="knop" data-a="volgende"' + (compleet ? ' data-in="volgende"' : ' disabled') + '>' + STRINGS.volgende + '</button>') +
          '</div></div></div>';

        /* — Voortgang: alle vijf stappen als tekst, de keuze erachter zodra hij er is — */
        const gekozenPerStap = {
          1: clubNaam(club),
          2: kv && kv.flex + ' · ' + kv.gewicht,
          3: kg && kg.titel,
          4: ka && [ka.merk, ka.generatie].filter(Boolean).join(' '),
        };
        const voortgang =
          '<ol class="voortgang" aria-label="' + STRINGS.voortgang + '">' +
          Array.from({ length: STAPPEN }, (_, i) => i + 1)
            .map(
              (n) =>
                '<li' + (n === stap ? ' aria-current="step" data-in="streep:' + n + '"' : n < stap ? ' class="gedaan"' : '') + '>' +
                n + ' ' + esc(STRINGS.stap_kort[n]) +
                (gekozenPerStap[n] ? '<span class="vk" data-in="vk:' + n + ':' + esc(gekozenPerStap[n]) + '">— ' + esc(gekozenPerStap[n]) + '</span>' : '') +
                '</li>'
            )
            .join('') +
          '</ol>';

        return (
          foutblok(melding) + foutblok(uit.fouten.length ? uit.fouten.join(' ') : null) +
          '<div class="wizard" data-stap="' + stap + '">' +
          '<p class="stapkop">' + STRINGS.stap_van(stap, STAPPEN) + '</p>' +
          voortgang +
          '<div class="stapraster' + (vlak ? ' stapraster--vlak' : '') + '">' + vlak +
          '<div class="stapinhoud">' +
          '<h2 class="staptitel" tabindex="-1" data-in="stap:' + stap + '">' + esc(titel) + '</h2>' +
          (intro ? '<p class="intro" data-in="intro:' + stap + '">' + esc(intro) + '</p>' : '') +
          '<div class="stap" data-in="body:' + stap + '">' + body + '</div>' +
          '</div></div>' +
          '</div>' +
          onderbalk
        );
      }
    }
  );
}
